import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import usePersistedState from './usePersistedState';
import useSubscription from './hooks/useSubscription';
import config from './config';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  Label,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  Rectangle,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ReferenceArea,
  ReferenceLine,
  // Componentes adicionales necesarios
  AreaChart,
  Area,
  ComposedChart,
  Brush
} from 'recharts';

// Importaciones de Lucide-React para iconos
import { 
  ArrowUpCircle, 
  ArrowDownCircle, 
  Minus, 
  AlertTriangle, 
  BarChart2, 
  TrendingUp, 
  Activity, 
  BookOpen,
  Calendar, 
  Info, 
  Download, 
  PieChart as PieChartIcon, 
  Share2, 
  Sliders,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Filter,
  RefreshCw,
  Eye,
  EyeOff,
  Save,
  FileText,
  HelpCircle,
  Search,
  Brain,
  TrendingUp as TrendingUpIcon,
  BarChart3,
  Clock,
  Maximize2,
  X,
  ChevronLeft,
  ChevronRight,
  Home,
  Trophy,
} from 'lucide-react';
import html2canvas from 'html2canvas';


function GenerateExam({ allQuestions, onStartExam, onBack, onGoHome, darkMode }) {
  // Hook de suscripción para obtener límites
  const { user, subscription } = useSubscription();
  
  // ============================
  // Estados para el examen
  // ============================
  const [examTitle, setExamTitle] = usePersistedState("examTitle", "", "examGen");
  const [selectedYears, setSelectedYears] = usePersistedState("selectedYears", [], "examGen");
  const [selectedEspecialidades, setSelectedEspecialidades] = usePersistedState("selectedEspecialidades", [], "examGen");
  const [selectedAreas, setSelectedAreas] = usePersistedState("selectedAreas", [], "examGen");
  const [selectedTopics, setSelectedTopics] = usePersistedState("selectedTopics", [], "examGen");
  const [selectedSubtopics, setSelectedSubtopics] = usePersistedState("selectedSubtopics", [], "examGen");
  // NUEVO: filtro para subtopic_specific
  const [selectedSubtopicsSpecific, setSelectedSubtopicsSpecific] = usePersistedState("selectedSubtopicsSpecific", [], "examGen");

  // Valor por defecto 50 (se ajustará después según el límite)
  const [numPreguntas, setNumPreguntas] = usePersistedState("numPreguntas", 5, "examGen");

  // Valor por defecto 60
  const [tiempo, setTiempo] = usePersistedState("tiempo", 60, "examGen");

  // Modo de examen
  const [examMode, setExamMode] = usePersistedState("examMode", "study", "examGen");

  // Orden de preguntas en modo simulación (true = aleatorio, false = fijo)
  const [shuffleQuestions, setShuffleQuestions] = usePersistedState("shuffleQuestions", true, "examGen");

  // ============================
  // Estados para fuente de preguntas (Real / IA / Simulacros / Mixto)
  // ============================
  const [questionSource, setQuestionSource] = usePersistedState("questionSource", "real", "examGen");
  const [aiPredictions, setAiPredictions] = useState([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);

  // ============================
  // Estados para Simulacros
  // ============================
  const [availableSimulacros, setAvailableSimulacros] = useState([]);
  const [selectedSimulacroId, setSelectedSimulacroId] = usePersistedState("selectedSimulacroId", null, "examGen");
  const [simulacroQuestions, setSimulacroQuestions] = useState([]);
  const [loadingSimulacros, setLoadingSimulacros] = useState(false);

  // Cargar lista de simulacros disponibles al montar
  useEffect(() => {
    const fetchSimulacros = async () => {
      try {
        const apiUrl = `${config.getApiUrl()}/simulacros`;

        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error('Error al cargar simulacros');

        const data = await response.json();
        setAvailableSimulacros(data);
      } catch (error) {
        console.error('Error fetching simulacros:', error);
        setAvailableSimulacros([]);
      }
    };

    fetchSimulacros();
  }, []);

  // Cargar preguntas del simulacro seleccionado
  useEffect(() => {
    const fetchSimulacroQuestions = async () => {
      if (questionSource !== 'simulacro' || !selectedSimulacroId) {
        setSimulacroQuestions([]);
        return;
      }

      setLoadingSimulacros(true);
      try {
        const apiUrl = `${config.getApiUrl()}/simulacros/${selectedSimulacroId}/questions`;

        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error('Error al cargar preguntas del simulacro');

        const data = await response.json();

        // Formatear al mismo formato que allQuestions
        const formattedQuestions = data.map((q, index) => ({
          id: `sim_${q.id}`,
          year: q.year || 'Simulacro',
          especialidad: q.especialidad,
          area: q.area,
          topic: q.topic,
          subtopic: q.subtopic,
          subtopic_specific: q.subtopic_specific,
          organ_system: q.organ_system,
          case_description: q.case_description,
          question_text: q.question_text,
          explanation: q.explanation,
          key_points: q.key_points,
          ascii_diagram: q.ascii_diagram,
          options: q.options || [],
          source: 'simulacro',
          question_number: q.question_number || (index + 1),
          simulacro_source: q.simulacro_source,
          simulacro_name: q.simulacro_name
        }));

        setSimulacroQuestions(formattedQuestions);
      } catch (error) {
        console.error('Error fetching simulacro questions:', error);
        setSimulacroQuestions([]);
      } finally {
        setLoadingSimulacros(false);
      }
    };

    fetchSimulacroQuestions();
  }, [questionSource, selectedSimulacroId]);

  // Cargar predicciones IA cuando se selecciona fuente 'ai' o 'mixed'
  useEffect(() => {
    const fetchPredictions = async () => {
      if (questionSource === 'real' || questionSource === 'simulacro') {
        setAiPredictions([]);
        return;
      }

      setLoadingPredictions(true);
      try {
        const apiUrl = `${config.getApiUrl()}/predictions`;

        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error('Error al cargar predicciones');

        const data = await response.json();

        // Formatear predicciones al mismo formato que allQuestions
        const formattedPredictions = data.map(pred => ({
          id: `ai_${pred.id}`,
          year: pred.year || 'Predicción IA',
          especialidad: pred.especialidad,
          area: pred.area,
          topic: pred.topic,
          subtopic: pred.subtopic,
          subtopic_specific: pred.subtopic_specific,
          organ_system: pred.organsystem,
          case_description: pred.case_description,
          question_text: pred.question_text,
          explanation: pred.explanation,
          key_points: pred.key_points,
          ascii_diagram: pred.ascii_diagram,
          options: pred.options || [],
          source: 'ai',
          ai_model: pred.ai_model
        }));

        setAiPredictions(formattedPredictions);
      } catch (error) {
        console.error('Error fetching AI predictions:', error);
        setAiPredictions([]);
      } finally {
        setLoadingPredictions(false);
      }
    };

    fetchPredictions();
  }, [questionSource]);

  // Migrar valores antiguos persistidos en localStorage ('ai', 'mixed')
  // a 'real' — ya no soportamos esas fuentes en la interfaz
  useEffect(() => {
    if (questionSource === 'ai' || questionSource === 'mixed') {
      setQuestionSource('real');
    }
  }, []); // solo al montar

  // Limpiar filtros seleccionados cuando cambia la fuente de preguntas
  // para que el usuario vea las nuevas opciones disponibles
  useEffect(() => {
    setSelectedYears([]);
    setSelectedEspecialidades([]);
    setSelectedAreas([]);
    setSelectedTopics([]);
    setSelectedSubtopics([]);
    setSelectedSubtopicsSpecific([]);
  }, [questionSource]);

  // Estados para búsqueda inteligente
  const [topicSearch, setTopicSearch] = useState("");
  const [subtopicSearch, setSubtopicSearch] = useState("");
  const [subtopicSpecificSearch, setSubtopicSpecificSearch] = useState("");
  
  // Estados para búsqueda global
  const [globalSearch, setGlobalSearch] = useState("");
const [globalSearchResults, setGlobalSearchResults] = useState([]);
const [isSearching, setIsSearching] = useState(false);
  const [globalSearchAreaFilter, setGlobalSearchAreaFilter] = useState("");
  const [globalSearchTopicFilter, setGlobalSearchTopicFilter] = useState("");
  const [globalSearchSubtopicFilter, setGlobalSearchSubtopicFilter] = useState("");
  const [globalSearchSubtopicSpecificFilter, setGlobalSearchSubtopicSpecificFilter] = useState("");
  
  // Estados para selección múltiple en búsqueda global
  const [selectedSearchResults, setSelectedSearchResults] = useState([]);

  // Estados para contador animado de preguntas filtradas (Floating Badge)
  const [prevFilteredCount, setPrevFilteredCount] = useState(0);
  const [animatedCount, setAnimatedCount] = useState(0);
  const [isCounterAnimating, setIsCounterAnimating] = useState(false);

  // ============================
  // SELECCIÓN DIRECTA — Búsqueda transversal
  // Cuando el usuario marca preguntas específicas en la búsqueda,
  // se guardan acá. Si tiene contenido, filteredQuestions las honra.
  // ============================
  const [directQuestionIds, setDirectQuestionIds] = useState(() => new Set());
  const directIdSet = directQuestionIds; // alias semántico
  const directCount = directQuestionIds.size;

  // Limpiar la selección directa cuando se resetea
  const clearDirectSelection = () => setDirectQuestionIds(new Set());

  // Toggle individual
  const toggleDirectSelection = (id) => {
    setDirectQuestionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const navButtonBase = 'inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-offset-2';
  const homeButtonStyles = darkMode
    ? 'bg-slate-900/70 border border-slate-700 text-slate-100 hover:bg-slate-800 focus:ring-blue-400 focus:ring-offset-slate-900'
    : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 focus:ring-blue-400 focus:ring-offset-white';
  const backButtonStyles = darkMode
    ? 'bg-gradient-to-r from-rose-500 via-amber-500 to-orange-500 text-white shadow-lg shadow-orange-500/20 hover:from-rose-400 hover:via-amber-400 hover:to-orange-400 focus:ring-rose-300 focus:ring-offset-slate-900'
    : 'bg-gradient-to-r from-rose-500 via-amber-400 to-orange-400 text-white shadow-md hover:from-rose-400 hover:via-amber-300 hover:to-orange-300 focus:ring-rose-200 focus:ring-offset-white';
  

  // AGREGAR AQUÍ: Control de darkMode y persistencia de estado
  // Control específico para el modo oscuro
  const [initialDarkModeSet, setInitialDarkModeSet] = useState(false);

  // Solución para scroll al cambiar darkMode
  useEffect(() => {
    if (!initialDarkModeSet) {
      setInitialDarkModeSet(true);
      return;
    }
    const scrollPosition = window.scrollY;
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 200);
  }, [darkMode]);



  // Colores para gráficos
  const barColors = [
    "#FF5733",
    "#33FF57",
    "#3357FF",
    "#FF33A1",
    "#A133FF",
    "#33FFF2",
    "#FF8F33",
    "#8F33FF"
  ];

  // ============================
  // Fuente de preguntas dinámica según selección (real/ai/mixed)
  // ============================
  const sourceQuestions = useMemo(() => {
    if (questionSource === 'ai') {
      return aiPredictions;
    } else if (questionSource === 'mixed') {
      return [...allQuestions, ...aiPredictions];
    } else if (questionSource === 'simulacro') {
      return simulacroQuestions;
    }
    return allQuestions; // 'real' por defecto
  }, [questionSource, allQuestions, aiPredictions, simulacroQuestions]);

  // ============================
  // Opciones únicas para filtros del examen (dinámicas según fuente)
  // ============================
  const uniqueYears = useMemo(
    () => Array.from(new Set(sourceQuestions.map((q) => q.year).filter(Boolean))).sort(),
    [sourceQuestions]
  );
  const uniqueEspecialidades = useMemo(
    () => Array.from(new Set(sourceQuestions.map((q) => q.especialidad).filter(Boolean))).sort(),
    [sourceQuestions]
  );
  const uniqueAreas = useMemo(
    () => Array.from(new Set(sourceQuestions.map((q) => q.area).filter(Boolean))).sort(),
    [sourceQuestions]
  );

  // ============================
  // Filtrado base (por Año, Especialidad y Área) - usa sourceQuestions
  // ============================
  const filteredBase = useMemo(() => {
    return sourceQuestions.filter((q) => {
      if (selectedYears.length > 0 && !selectedYears.includes(q.year)) return false;
      if (selectedEspecialidades.length > 0 && !selectedEspecialidades.includes(q.especialidad)) return false;
      if (selectedAreas.length > 0 && !selectedAreas.includes(q.area)) return false;
      return true;
    });
  }, [sourceQuestions, selectedYears, selectedEspecialidades, selectedAreas]);

  // ============================
  // Cálculo y ordenamiento de Temas (con contador)
  // ============================
  const topicsWithCount = useMemo(() => {
    const counts = {};
    filteredBase.forEach((q) => {
      const topic = q.topic;
      if (topic) { // Asegurarse de que topic no sea undefined o null
        counts[topic] = (counts[topic] || 0) + 1;
      }
    });
    const arr = Object.keys(counts).map((topic) => ({ topic, count: counts[topic] }));
    // Orden: primero desc por count, luego alfabético por tema
    arr.sort((a, b) => b.count - a.count || a.topic.localeCompare(b.topic));
    
    // Debug: verificar si Misceláneas está disponible
    const miscelaneasTopics = arr.filter(item => item.topic.includes('Misceláneas'));
    if (miscelaneasTopics.length > 0) {
      console.log('Temas con Misceláneas disponibles:', miscelaneasTopics);
    }
    
    return arr;
  }, [filteredBase]);

  const availableTopics = useMemo(() => topicsWithCount.map((item) => item.topic), [topicsWithCount]);

  useEffect(() => {
    setSelectedTopics((prev) => prev.filter((tp) => availableTopics.includes(tp)));
  }, [availableTopics]);

  // Filtrar temas basado en búsqueda
  const filteredTopicsWithCount = useMemo(() => {
    if (!topicSearch.trim()) return topicsWithCount;
    const searchLower = topicSearch.toLowerCase();
    return topicsWithCount.filter(item => 
      item.topic.toLowerCase().includes(searchLower)
    );
  }, [topicsWithCount, topicSearch]);

  const topicPreviewMap = useMemo(() => {
    const map = {};
    filteredBase.forEach((q) => {
      const topic = q.topic || 'Sin tema';
      if (!map[topic]) {
        map[topic] = { total: 0, subtopics: {} };
      }
      map[topic].total += 1;
      if (q.subtopic) {
        map[topic].subtopics[q.subtopic] = (map[topic].subtopics[q.subtopic] || 0) + 1;
      }
    });

    const result = {};
    Object.keys(map).forEach((topic) => {
      const topSubtopics = Object.entries(map[topic].subtopics || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([name, count]) => ({ name, count }));
      result[topic] = {
        total: map[topic].total,
        topSubtopics,
      };
    });

    return result;
  }, [filteredBase]);

  const maxTopicCount = useMemo(
    () => (filteredTopicsWithCount.length > 0 ? filteredTopicsWithCount[0].count : 1),
    [filteredTopicsWithCount]
  );

  // ============================
  // Filtrado para Subtemas según los Temas seleccionados
  // ============================
  const filteredForSubtopics = useMemo(() => {
    let data = filteredBase;
    if (selectedTopics.length > 0) {
      data = data.filter((q) => selectedTopics.includes(q.topic));
      // Debug para Misceláneas
      if (selectedTopics.some(t => t.includes('Misceláneas'))) {
        console.log('Debug Misceláneas:');
        console.log('- Temas seleccionados:', selectedTopics);
        console.log('- Total preguntas en filteredBase:', filteredBase.length);
        console.log('- Preguntas con Misceláneas:', filteredBase.filter(q => q.topic && q.topic.includes('Misceláneas')).length);
        console.log('- Preguntas después del filtro:', data.length);
        console.log('- Primeras 3 preguntas con Misceláneas:', 
          filteredBase.filter(q => q.topic && q.topic.includes('Misceláneas')).slice(0, 3).map(q => ({
            topic: q.topic,
            subtopic: q.subtopic,
            area: q.area
          }))
        );
      }
    }
    return data;
  }, [filteredBase, selectedTopics]);

  // Cálculo y ordenamiento de Subtemas (con contador)
  const subtopicsWithCount = useMemo(() => {
    const counts = {};
    filteredForSubtopics.forEach((q) => {
      const subtopic = q.subtopic;
      if (subtopic) { // Asegurarse de que subtopic no sea undefined o null
        counts[subtopic] = (counts[subtopic] || 0) + 1;
      }
    });
    const arr = Object.keys(counts).map((subtopic) => ({ subtopic, count: counts[subtopic] }));
    // Orden: primero desc por count, luego alfabético por subtema
    arr.sort((a, b) => b.count - a.count || a.subtopic.localeCompare(b.subtopic));
    
    // Debug: mostrar subtemas cuando se selecciona Misceláneas o Intoxicaciones
    if (selectedTopics.some(t => t.includes('Misceláneas') || t.includes('Intoxicaciones'))) {
      console.log('Subtemas encontrados para', selectedTopics, ':', arr.slice(0, 10));
      // Buscar específicamente subtemas con caústico
      const causticoSubtemas = arr.filter(item => 
        item.subtopic.toLowerCase().includes('caústic') || 
        item.subtopic.toLowerCase().includes('caustic')
      );
      if (causticoSubtemas.length > 0) {
        console.log('Subtemas con caústico encontrados:', causticoSubtemas);
      }
    }
    
    return arr;
  }, [filteredForSubtopics, selectedTopics]);

  const availableSubtopics = useMemo(
    () => subtopicsWithCount.map((item) => item.subtopic),
    [subtopicsWithCount]
  );

  useEffect(() => {
    setSelectedSubtopics((prev) => prev.filter((st) => availableSubtopics.includes(st)));
  }, [availableSubtopics]);

  // Filtrar subtemas basado en búsqueda
  const filteredSubtopicsWithCount = useMemo(() => {
    if (!subtopicSearch.trim()) return subtopicsWithCount;
    const searchLower = subtopicSearch.toLowerCase();
    const filtered = subtopicsWithCount.filter(item => 
      item.subtopic.toLowerCase().includes(searchLower)
    );
    
    // Debug para búsqueda de caústico
    if (searchLower.includes('caústico') || searchLower.includes('caustico')) {
      console.log('=== Debug búsqueda caústico ===');
      console.log('Término de búsqueda:', searchLower);
      console.log('Total subtemas disponibles:', subtopicsWithCount.length);
      console.log('Subtemas que contienen "caústic":', 
        subtopicsWithCount.filter(item => 
          item.subtopic.toLowerCase().includes('caústic') || 
          item.subtopic.toLowerCase().includes('caustic')
        )
      );
      console.log('Resultados de búsqueda:', filtered);
      console.log('Temas seleccionados:', selectedTopics);
      // Mostrar algunos subtemas disponibles
      console.log('Primeros 10 subtemas disponibles:', subtopicsWithCount.slice(0, 10));
    }
    
    return filtered;
  }, [subtopicsWithCount, subtopicSearch, selectedTopics]);

  // ============================
  // Filtrado para Subtema Específico (subtopic_specific)
  // ============================
  const filteredForSubtopicsSpecific = useMemo(() => {
    let data = filteredForSubtopics;
    if (selectedSubtopics.length > 0) {
      data = data.filter((q) => selectedSubtopics.includes(q.subtopic));
    }
    return data;
  }, [filteredForSubtopics, selectedSubtopics]);

  const subtopicsSpecificWithCount = useMemo(() => {
    const counts = {};
    filteredForSubtopicsSpecific.forEach((q) => {
      const sps = q.subtopic_specific || "Sin especificar";
      counts[sps] = (counts[sps] || 0) + 1;
    });
    const arr = Object.keys(counts).map((sps) => ({
      subtopic_specific: sps,
      count: counts[sps]
    }));
    // Orden: desc por count, luego alfabético
    arr.sort((a, b) => b.count - a.count || a.subtopic_specific.localeCompare(b.subtopic_specific));
    return arr;
  }, [filteredForSubtopicsSpecific]);

  const availableSubtopicsSpecific = useMemo(
    () => subtopicsSpecificWithCount.map((item) => item.subtopic_specific),
    [subtopicsSpecificWithCount]
  );

  useEffect(() => {
    setSelectedSubtopicsSpecific((prev) =>
      prev.filter((sps) => availableSubtopicsSpecific.includes(sps))
    );
  }, [availableSubtopicsSpecific]);

  // Filtrar subtemas específicos basado en búsqueda
  const filteredSubtopicsSpecificWithCount = useMemo(() => {
    if (!subtopicSpecificSearch.trim()) return subtopicsSpecificWithCount;
    const searchLower = subtopicSpecificSearch.toLowerCase();
    return subtopicsSpecificWithCount.filter(item => 
      item.subtopic_specific.toLowerCase().includes(searchLower)
    );
  }, [subtopicsSpecificWithCount, subtopicSpecificSearch]);

  // ============================
  // Filtrado final de preguntas (aplicando TODOS los filtros)
  // Usa sourceQuestions que ya está filtrado por fuente (real/ai/mixed)
  //
  // NUEVO: si directQuestionIds tiene contenido (selección directa de la
  // búsqueda transversal), filtra solo a esas preguntas — pero los demás
  // filtros (año/especialidad/área) se mantienen como refinamiento.
  // ============================
  const filteredQuestions = useMemo(() => {
    const useDirect = directQuestionIds.size > 0;
    return sourceQuestions.filter((q) => {
      if (useDirect && !directQuestionIds.has(q.id)) return false;
      if (selectedYears.length > 0 && !selectedYears.includes(q.year)) return false;
      if (selectedEspecialidades.length > 0 && !selectedEspecialidades.includes(q.especialidad)) return false;
      if (selectedAreas.length > 0 && !selectedAreas.includes(q.area)) return false;
      // Si hay selección directa, ignoramos los filtros jerárquicos clásicos
      // (porque el usuario eligió las preguntas explícitamente).
      if (!useDirect) {
        if (selectedTopics.length > 0 && !selectedTopics.includes(q.topic)) return false;
        if (selectedSubtopics.length > 0 && !selectedSubtopics.includes(q.subtopic)) return false;
        if (selectedSubtopicsSpecific.length > 0 && !selectedSubtopicsSpecific.includes(q.subtopic_specific)) {
          return false;
        }
      }
      return true;
    });
  }, [
    sourceQuestions,
    directQuestionIds,
    selectedYears,
    selectedEspecialidades,
    selectedAreas,
    selectedTopics,
    selectedSubtopics,
    selectedSubtopicsSpecific
  ]);

  // ============================
  // Determinar el límite máximo de preguntas según el plan
  // ============================
  const maxAllowed = useMemo(() => {
    if (subscription?.isAdmin || subscription?.plan === 'pro') {
      return filteredQuestions.length || 1000; // Sin límite para admin y PRO
    } else if (user) {
      return 15; // Usuario gratuito registrado: 15 preguntas
    } else {
      return 5; // Usuario no registrado: 5 preguntas
    }
  }, [subscription, user, filteredQuestions.length]);

  // Ajustar numPreguntas para que coincida con la cantidad disponible tras filtros (respetando el límite del plan)
  useEffect(() => {
    const available = Math.min(filteredQuestions.length, maxAllowed);
    if (numPreguntas !== available) {
      setNumPreguntas(available);
    }
  }, [filteredQuestions.length, maxAllowed, numPreguntas, setNumPreguntas]);

  // ============================
  // Efecto para animar el contador cuando cambia filteredQuestions
  // ============================
  useEffect(() => {
    const targetCount = filteredQuestions.length;

    if (targetCount !== prevFilteredCount) {
      setIsCounterAnimating(true);
      const startCount = animatedCount;
      const diff = targetCount - startCount;
      const duration = 600; // ms
      const steps = 30;
      const stepDuration = duration / steps;
      let currentStep = 0;

      const animate = () => {
        currentStep++;
        const progress = currentStep / steps;
        // Easing function (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const newCount = Math.round(startCount + diff * easeOut);
        setAnimatedCount(newCount);

        if (currentStep < steps) {
          setTimeout(animate, stepDuration);
        } else {
          setAnimatedCount(targetCount);
          setPrevFilteredCount(targetCount);
          setTimeout(() => setIsCounterAnimating(false), 300);
        }
      };

      animate();
    }
  }, [filteredQuestions.length, prevFilteredCount, animatedCount]);

  // ============================
  // Función para alternar selección en checkboxes
  // ============================
  const toggleSelected = (value, selectedArray, setSelected) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  // ============================
  // Datos filtrados para búsqueda global
  // ============================
  
  // Temas disponibles para búsqueda según área
  const globalSearchAvailableTopics = useMemo(() => {
    let questions = allQuestions || [];
    if (globalSearchAreaFilter) {
      questions = questions.filter(q => q.area === globalSearchAreaFilter);
    }
    const topics = [...new Set(questions.map(q => q.topic))].sort();
    return topics;
  }, [allQuestions, globalSearchAreaFilter]);
  
  // Subtemas disponibles para búsqueda según tema
  const globalSearchAvailableSubtopics = useMemo(() => {
    let questions = allQuestions || [];
    if (globalSearchAreaFilter) {
      questions = questions.filter(q => q.area === globalSearchAreaFilter);
    }
    if (globalSearchTopicFilter) {
      questions = questions.filter(q => q.topic === globalSearchTopicFilter);
    }
    const subtopics = [...new Set(questions.map(q => q.subtopic))].sort();
    return subtopics;
  }, [allQuestions, globalSearchAreaFilter, globalSearchTopicFilter]);
  
  // Subtemas específicos disponibles para búsqueda
  const globalSearchAvailableSubtopicsSpecific = useMemo(() => {
    let questions = allQuestions || [];
    if (globalSearchAreaFilter) {
      questions = questions.filter(q => q.area === globalSearchAreaFilter);
    }
    if (globalSearchTopicFilter) {
      questions = questions.filter(q => q.topic === globalSearchTopicFilter);
    }
    if (globalSearchSubtopicFilter) {
      questions = questions.filter(q => q.subtopic === globalSearchSubtopicFilter);
    }
    const subtopicsSpecific = [...new Set(questions.map(q => q.subtopic_specific || 'Sin especificar'))].sort();
    return subtopicsSpecific;
  }, [allQuestions, globalSearchAreaFilter, globalSearchTopicFilter, globalSearchSubtopicFilter]);

  // ============================
  // Función de búsqueda global (TRANSVERSAL)
  // Respeta los filtros del panel principal (año/especialidad/área)
  // y los filtros locales del propio buscador
  // ============================
  const performGlobalSearch = useCallback(() => {
    if (!globalSearch.trim()) {
      setGlobalSearchResults([]);
      return;
    }

    setIsSearching(true);
    const searchTerm = globalSearch.toLowerCase();
    const results = [];

    // Punto de partida: usar la fuente activa, no allQuestions
    let questionsToSearch = sourceQuestions;

    // FIX BUG 3: Respetar filtros del panel principal
    if (selectedYears.length > 0) {
      questionsToSearch = questionsToSearch.filter(q => selectedYears.includes(q.year));
    }
    if (selectedEspecialidades.length > 0) {
      questionsToSearch = questionsToSearch.filter(q => selectedEspecialidades.includes(q.especialidad));
    }
    if (selectedAreas.length > 0) {
      questionsToSearch = questionsToSearch.filter(q => selectedAreas.includes(q.area));
    }

    // Filtros locales del buscador (mantener compatibilidad)
    if (globalSearchAreaFilter) {
      questionsToSearch = questionsToSearch.filter(q => q.area === globalSearchAreaFilter);
    }
    if (globalSearchTopicFilter) {
      questionsToSearch = questionsToSearch.filter(q => q.topic === globalSearchTopicFilter);
    }
    if (globalSearchSubtopicFilter) {
      questionsToSearch = questionsToSearch.filter(q => q.subtopic === globalSearchSubtopicFilter);
    }
    if (globalSearchSubtopicSpecificFilter) {
      questionsToSearch = questionsToSearch.filter(q => q.subtopic_specific === globalSearchSubtopicSpecificFilter);
    }

    questionsToSearch.forEach((question) => {
      // Obtener opciones de respuesta como array de strings
      const answerTexts = question.options ? 
        question.options.map(opt => opt.option_text || '') : 
        (question.answers || []);
      
      const searchableFields = [
        question.question_text || question.question || '',
        question.case_description || '',
        question.topic || '',
        question.subtopic || '',
        question.subtopic_specific || '',
        question.area || '',
        question.especialidad || '',
        ...answerTexts,
        question.explanation || ''
      ];

      const matches = searchableFields.some(field => 
        field.toLowerCase().includes(searchTerm)
      );

      if (matches) {
        // Determinar dónde se encontró la coincidencia
        const matchedIn = [];
        const questionText = question.question_text || question.question || '';
        if (questionText && questionText.toLowerCase().includes(searchTerm)) {
          matchedIn.push('pregunta');
        }
        if (question.case_description && question.case_description.toLowerCase().includes(searchTerm)) {
          matchedIn.push('caso clínico');
        }
        if (answerTexts.some(a => a.toLowerCase().includes(searchTerm))) {
          matchedIn.push('respuestas');
        }
        if (question.explanation && question.explanation.toLowerCase().includes(searchTerm)) {
          matchedIn.push('explicación');
        }

        results.push({
          ...question,
          matchedIn: matchedIn.join(', '),
          relevance: matchedIn.length
        });
      }
    });

    // Ordenar por relevancia (más coincidencias primero)
    results.sort((a, b) => b.relevance - a.relevance);

    setGlobalSearchResults(results);
    setIsSearching(false);
  }, [
    globalSearch,
    sourceQuestions,
    selectedYears,
    selectedEspecialidades,
    selectedAreas,
    globalSearchAreaFilter,
    globalSearchTopicFilter,
    globalSearchSubtopicFilter,
    globalSearchSubtopicSpecificFilter,
  ]);

  // Ejecutar búsqueda cuando cambie el término o los filtros del panel
  useEffect(() => {
    const timer = setTimeout(() => {
      performGlobalSearch();
    }, 250); // Debounce 250ms

    return () => clearTimeout(timer);
  }, [
    globalSearch,
    selectedYears,
    selectedEspecialidades,
    selectedAreas,
    globalSearchAreaFilter,
    globalSearchTopicFilter,
    globalSearchSubtopicFilter,
    globalSearchSubtopicSpecificFilter,
    performGlobalSearch,
  ]);
  
  // Limpiar selección cuando cambien los resultados de búsqueda
  useEffect(() => {
    setSelectedSearchResults([]);
  }, [globalSearchResults]);

  // ============================
  // BÚSQUEDA TRANSVERSAL — agrupación jerárquica de resultados
  // ============================
  const groupedSearchResults = useMemo(() => {
    if (!globalSearchResults || globalSearchResults.length === 0) return [];

    const groups = {};
    globalSearchResults.forEach((q) => {
      const area = q.area || 'Sin área';
      const topic = q.topic || 'Sin tema';
      const subtopic = q.subtopic || 'Sin subtema';

      if (!groups[area]) {
        groups[area] = { area, count: 0, topics: {} };
      }
      if (!groups[area].topics[topic]) {
        groups[area].topics[topic] = { topic, count: 0, subtopics: {} };
      }
      if (!groups[area].topics[topic].subtopics[subtopic]) {
        groups[area].topics[topic].subtopics[subtopic] = { subtopic, count: 0, questions: [] };
      }

      groups[area].count += 1;
      groups[area].topics[topic].count += 1;
      groups[area].topics[topic].subtopics[subtopic].count += 1;
      groups[area].topics[topic].subtopics[subtopic].questions.push(q);
    });

    return Object.values(groups)
      .map((a) => ({
        ...a,
        topics: Object.values(a.topics)
          .map((t) => ({
            ...t,
            subtopics: Object.values(t.subtopics).sort((x, y) => y.count - x.count),
          }))
          .sort((x, y) => y.count - x.count),
      }))
      .sort((x, y) => y.count - x.count);
  }, [globalSearchResults]);

  // Agregar TODAS las preguntas encontradas a la selección directa
  const addAllSearchToDirect = useCallback(() => {
    if (globalSearchResults.length === 0) return;
    setDirectQuestionIds((prev) => {
      const next = new Set(prev);
      globalSearchResults.forEach((q) => next.add(q.id));
      return next;
    });
  }, [globalSearchResults]);

  // Agregar SOLO las preguntas marcadas (selectedSearchResults) a la selección directa
  const addSelectedSearchToDirect = useCallback(() => {
    if (selectedSearchResults.length === 0) return;
    setDirectQuestionIds((prev) => {
      const next = new Set(prev);
      selectedSearchResults.forEach((q) => next.add(q.id));
      return next;
    });
    setSelectedSearchResults([]);
  }, [selectedSearchResults]);

  // ¿Cuántas preguntas de los resultados ya están en la selección directa?
  const searchAlreadyInDirect = useMemo(() => {
    if (!globalSearchResults.length) return 0;
    let n = 0;
    for (const q of globalSearchResults) if (directIdSet.has(q.id)) n++;
    return n;
  }, [globalSearchResults, directIdSet]);


  // ============================
  // Funciones para seleccionar todos/limpiar filtros individuales
  // ============================
  const selectAllYears = () => {
    setSelectedYears([...uniqueYears]);
  };

  const clearYears = () => {
    setSelectedYears([]);
  };

  const selectLastFiveYears = () => {
    const lastFiveYears = uniqueYears.filter(year => {
      const yearMatch = year.match(/\d{4}/);
      if (yearMatch) {
        const yearNum = parseInt(yearMatch[0]);
        return yearNum >= 2020 && yearNum <= 2024;
      }
      return false;
    });
    setSelectedYears(lastFiveYears);
  };

  const selectAllSpecialties = () => {
    setSelectedEspecialidades([...uniqueEspecialidades]);
  };

  const clearSpecialties = () => {
    setSelectedEspecialidades([]);
  };

  const selectAllAreas = () => {
    setSelectedAreas([...uniqueAreas]);
  };

  const clearAreas = () => {
    setSelectedAreas([]);
  };

  // ============================
  // Mapeos para auto-selección
  // ============================
  // Crear mapeo de subtemas a sus temas padres
  const subtopicToTopicMap = useMemo(() => {
    const map = {};
    allQuestions.forEach(q => {
      if (q.subtopic && q.topic) {
        map[q.subtopic] = q.topic;
      }
    });
    return map;
  }, [allQuestions]);

  // Crear mapeo de subtemas específicos a sus subtemas y temas padres
  const subtopicSpecificToParentsMap = useMemo(() => {
    const map = {};
    allQuestions.forEach(q => {
      if (q.subtopic_specific && q.subtopic && q.topic) {
        if (!map[q.subtopic_specific]) {
          map[q.subtopic_specific] = {
            subtopic: q.subtopic,
            topic: q.topic
          };
        }
      }
    });
    return map;
  }, [allQuestions]);

  // ============================
  // Handlers de selección visual
  // ============================
  const toggleTopic = (topic) => {
    setSelectedTopics((prev) => {
      const isSelected = prev.includes(topic);
      if (isSelected) {
        setSelectedSubtopics((prevSubs) => prevSubs.filter((sub) => subtopicToTopicMap[sub] !== topic));
        setSelectedSubtopicsSpecific((prevSps) =>
          prevSps.filter((sps) => subtopicSpecificToParentsMap[sps]?.topic !== topic)
        );
        return prev.filter((t) => t !== topic);
      }
      return [...prev, topic];
    });
  };

  const toggleSubtopic = (subtopic) => {
    const parentTopic = subtopicToTopicMap[subtopic];
    setSelectedSubtopics((prev) => {
      const isSelected = prev.includes(subtopic);
      if (isSelected) {
        setSelectedSubtopicsSpecific((prevSps) =>
          prevSps.filter((sps) => subtopicSpecificToParentsMap[sps]?.subtopic !== subtopic)
        );
        return prev.filter((item) => item !== subtopic);
      }
      if (parentTopic) {
        setSelectedTopics((prevTopics) =>
          prevTopics.includes(parentTopic) ? prevTopics : [...prevTopics, parentTopic]
        );
      }
      return [...prev, subtopic];
    });
  };

  const toggleSubtopicSpecific = (subtopicSpecific) => {
    const parents = subtopicSpecificToParentsMap[subtopicSpecific];
    setSelectedSubtopicsSpecific((prev) => {
      const isSelected = prev.includes(subtopicSpecific);
      if (isSelected) {
        return prev.filter((item) => item !== subtopicSpecific);
      }
      if (parents?.subtopic) {
        setSelectedSubtopics((prevSubs) =>
          prevSubs.includes(parents.subtopic) ? prevSubs : [...prevSubs, parents.subtopic]
        );
      }
      if (parents?.topic) {
        setSelectedTopics((prevTopics) =>
          prevTopics.includes(parents.topic) ? prevTopics : [...prevTopics, parents.topic]
        );
      }
      return [...prev, subtopicSpecific];
    });
  };

  // ============================
  // Función para limpiar filtros del examen
  // ============================
  const resetFilters = () => {
    setSelectedYears([]);
    setSelectedEspecialidades([]);
    setSelectedAreas([]);
    setSelectedTopics([]);
    setSelectedSubtopics([]);
    setSelectedSubtopicsSpecific([]); // Limpiar también subtopic_specific
    setQuestionSource('real'); // Resetear a preguntas reales
    setDirectQuestionIds(new Set()); // Limpiar selección directa
    setGlobalSearch(''); // Limpiar búsqueda
  };

  // ============================
  // Limpiezas granulares por sección
  // ============================
  const clearRangeFilters = () => {
    setSelectedYears([]);
    setSelectedEspecialidades([]);
    setSelectedAreas([]);
  };

  const clearHierarchyFilters = () => {
    setSelectedTopics([]);
    setSelectedSubtopics([]);
    setSelectedSubtopicsSpecific([]);
  };

  // Conteos por sección (para mostrar/ocultar botones de limpiar)
  const rangeFiltersCount =
    selectedYears.length + selectedEspecialidades.length + selectedAreas.length;
  const hierarchyFiltersCount =
    selectedTopics.length + selectedSubtopics.length + selectedSubtopicsSpecific.length;
  const totalFiltersCount =
    rangeFiltersCount + hierarchyFiltersCount + (directCount > 0 ? 1 : 0);



  // ============================
  // Handler para iniciar el examen
  // ============================
  const handleStart = () => {
    const limit = numPreguntas;
    let finalSelection;

    // Determinar si debemos aleatorizar
    const shouldShuffle = examMode === "simulation" && shuffleQuestions;

    if (shouldShuffle) {
      // Aleatorizar las preguntas
      const shuffled = [...filteredQuestions].sort(() => Math.random() - 0.5);
      finalSelection = shuffled.slice(0, limit);
    } else {
      // Mantener orden original (modo estudio o simulación con orden fijo)
      finalSelection = filteredQuestions.slice(0, limit);
    }

    // Llamamos al callback pasando examTitle y preserveOrder
    // preserveOrder = true cuando NO queremos que el Quiz vuelva a aleatorizar
    onStartExam(
      finalSelection,
      examMode === "simulation" ? tiempo : null,
      examMode,
      examTitle,
      !shouldShuffle, // preserveOrder: true si no aleatorizamos aquí
      questionSource === 'simulacro' ? selectedSimulacroId : null
    );
  };


  // ============================
  // Handlers para selección múltiple
  // ============================
  const handleSelectResult = (result) => {
    setSelectedSearchResults(prev => {
      const isSelected = prev.some(r => r.id === result.id);
      if (isSelected) {
        return prev.filter(r => r.id !== result.id);
      } else {
        return [...prev, result];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSearchResults.length === globalSearchResults.slice(0, 20).length) {
      setSelectedSearchResults([]);
    } else {
      setSelectedSearchResults(globalSearchResults.slice(0, 20));
    }
  };

  const handleBulkApplyFilters = () => {
    const years = new Set(selectedYears);
    const areas = new Set(selectedAreas);
    const topics = new Set(selectedTopics);
    const subtopics = new Set(selectedSubtopics);
    const subtopicsSpecific = new Set(selectedSubtopicsSpecific);

    selectedSearchResults.forEach(result => {
      if (result.year) years.add(result.year);
      if (result.area) areas.add(result.area);
      if (result.topic) topics.add(result.topic);
      if (result.subtopic) subtopics.add(result.subtopic);
      if (result.subtopic_specific) subtopicsSpecific.add(result.subtopic_specific);
    });

    setSelectedYears(Array.from(years));
    setSelectedAreas(Array.from(areas));
    setSelectedTopics(Array.from(topics));
    setSelectedSubtopics(Array.from(subtopics));
    setSelectedSubtopicsSpecific(Array.from(subtopicsSpecific));
    
    // Limpiar búsqueda y selección
    setGlobalSearch('');
    setSelectedSearchResults([]);
  };

  // ============================
  // Tooltips y handlers
  // ============================
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-300 p-2 rounded shadow">
          <p className="font-bold text-gray-800">Subtema: {label}</p>
          <p className="text-gray-600">Preguntas: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  // ===========================================================================
  // INTERFAZ "CLINICAL EDITORIAL — MIDNIGHT INK"
  // Inspirado en Cambridge Press + revistas científicas + diseño editorial
  // Tipografía: Newsreader (serif), Manrope (sans), Geist Mono (mono)
  // Acento: midnight blue — intelectual, premium, distintivo
  // ===========================================================================

  // Tokens de diseño — modo oscuro cinemático / modo claro editorial cream
  const T = darkMode
    ? {
        // Modo oscuro — onyx con calor de cream sobre azul lit
        paper: '#07090D',           // onyx con tinte azul casi imperceptible
        paperSoft: '#0F131A',       // superficies elevadas
        paperWarm: '#181D27',       // hover/highlight
        paperLift: '#1F2533',       // segundo nivel de elevación
        ink: '#F2EDE0',             // cream cálido (contraste con azul frío)
        inkSoft: '#B5AC9A',         // secundario legible
        inkMute: '#6A6356',         // labels
        hairline: '#2B3140',        // ash azulado visible
        hairlineSoft: '#1A1F2A',    // divisorias suaves
        accent: '#7DA5D9',          // azul luminoso lit-from-within
        accentDeep: '#4F7AB8',      // hover/active
        accentSoft: '#0F1A2E',      // tinted bg banner
        accentTint: '#0A1424',      // tint apenas visible
        sage: '#8DA294',            // success
        glow: 'rgba(125, 165, 217, 0.22)',
      }
    : {
        // Modo claro — vellum editorial con tinta azul Cambridge
        paper: '#F4EFE3',           // cream warm vellum
        paperSoft: '#FBF8F1',       // superficie elevada
        paperWarm: '#EDE5D2',       // hover warm
        paperLift: '#E5DCC4',       // segundo nivel
        ink: '#0E1218',             // black-blue (no negro puro)
        inkSoft: '#3E4654',         // secundario
        inkMute: '#7A8499',         // labels
        hairline: '#D8D2C5',        // hairline cream
        hairlineSoft: '#E5DFD2',    // suave
        accent: '#1E3A6F',          // deep ink blue Cambridge
        accentDeep: '#0F2147',      // muy oscuro para hover/active
        accentSoft: '#E8EEF7',      // tint clara para banner
        accentTint: '#F2F5FB',      // muy clara
        sage: '#506B5C',
        glow: 'rgba(30, 58, 111, 0.16)',
      };

  // Animación staggered para el reveal de secciones
  const sectionFade = (delay = 0) => ({
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] },
  });

  // Componente helper: encabezado de sección editorial
  const SectionMarker = ({ number, title, subtitle, hint, accent = false }) => (
    <header className="flex items-baseline gap-5 mb-5">
      <div
        className="font-mono text-[10px] uppercase tracking-[0.28em] tabular-nums whitespace-nowrap pt-1"
        style={{
          color: accent ? T.accent : T.inkMute,
          textShadow: accent && darkMode ? `0 0 14px ${T.glow}` : 'none',
        }}
      >
        § {number}
      </div>
      <div className="flex-1 min-w-0">
        <h2
          className="font-serif text-[1.875rem] leading-[1.05] tracking-[-0.01em]"
          style={{
            color: T.ink,
            fontFamily: '"Newsreader", serif',
            fontStyle: accent ? 'italic' : 'normal',
            fontWeight: 400,
            textShadow: darkMode ? `0 0 28px rgba(244, 239, 229, 0.06)` : 'none',
          }}
        >
          {title}
        </h2>
        {subtitle && (
          <p
            className="text-sm mt-1"
            style={{ color: T.inkSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {hint && (
        <div
          className="font-mono text-[10px] uppercase tracking-[0.18em] tabular-nums whitespace-nowrap pt-1"
          style={{ color: T.inkMute }}
        >
          {hint}
        </div>
      )}
    </header>
  );

  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (
    <>
      {/* Estilos locales del módulo editorial */}
      <style>{`
        @keyframes mep-grain {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(-1%, 1%); }
          50% { transform: translate(1%, -1%); }
          75% { transform: translate(-0.5%, -0.5%); }
        }
        @keyframes mep-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.85); }
        }
        @keyframes mep-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        /* ===== PHOENIX FIRE — hover dramático ===== */
        @keyframes mep-phoenix-flicker {
          0%, 100% {
            box-shadow:
              0 0 0 1px ${T.accent}40,
              0 0 18px -2px ${darkMode ? 'rgba(125, 165, 217, 0.45)' : 'rgba(30, 58, 111, 0.28)'},
              0 0 36px -4px ${darkMode ? 'rgba(125, 165, 217, 0.32)' : 'rgba(30, 58, 111, 0.18)'},
              0 0 70px -8px ${darkMode ? 'rgba(125, 165, 217, 0.22)' : 'rgba(30, 58, 111, 0.10)'},
              inset 0 1px 0 ${darkMode ? 'rgba(173, 200, 235, 0.10)' : 'rgba(255, 255, 255, 0.6)'};
          }
          33% {
            box-shadow:
              0 0 0 1px ${T.accent}50,
              0 0 22px -2px ${darkMode ? 'rgba(157, 191, 232, 0.55)' : 'rgba(48, 84, 141, 0.32)'},
              0 0 44px -4px ${darkMode ? 'rgba(125, 165, 217, 0.40)' : 'rgba(30, 58, 111, 0.22)'},
              0 0 84px -8px ${darkMode ? 'rgba(125, 165, 217, 0.28)' : 'rgba(30, 58, 111, 0.14)'},
              inset 0 1px 0 ${darkMode ? 'rgba(173, 200, 235, 0.12)' : 'rgba(255, 255, 255, 0.65)'};
          }
          66% {
            box-shadow:
              0 0 0 1px ${T.accent}40,
              0 0 16px -2px ${darkMode ? 'rgba(125, 165, 217, 0.50)' : 'rgba(30, 58, 111, 0.30)'},
              0 0 38px -4px ${darkMode ? 'rgba(157, 191, 232, 0.36)' : 'rgba(48, 84, 141, 0.20)'},
              0 0 76px -8px ${darkMode ? 'rgba(125, 165, 217, 0.24)' : 'rgba(30, 58, 111, 0.12)'},
              inset 0 1px 0 ${darkMode ? 'rgba(173, 200, 235, 0.10)' : 'rgba(255, 255, 255, 0.6)'};
          }
        }
        .mep-phoenix {
          position: relative;
          isolation: isolate;
        }
        .mep-phoenix::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.45s ease-out;
          background:
            radial-gradient(ellipse 90% 70% at 50% 100%,
              ${darkMode ? 'rgba(125, 165, 217, 0.20)' : 'rgba(30, 58, 111, 0.10)'} 0%,
              transparent 60%
            ),
            radial-gradient(ellipse 120% 50% at 0% 50%,
              ${darkMode ? 'rgba(157, 191, 232, 0.10)' : 'rgba(48, 84, 141, 0.06)'} 0%,
              transparent 55%
            );
          z-index: 0;
        }
        .mep-phoenix:hover {
          animation: mep-phoenix-flicker 2.6s ease-in-out infinite;
          transform: translateY(-3px);
        }
        .mep-phoenix:hover::before {
          opacity: 1;
        }
        .mep-phoenix > * {
          position: relative;
          z-index: 2;
        }

        /* ===== CARDS DE CONTROL ===== */
        .mep-control-card {
          background: ${darkMode
            ? `linear-gradient(165deg, ${T.paperSoft} 0%, ${T.paper} 100%)`
            : `linear-gradient(165deg, ${T.paperSoft} 0%, ${T.paperWarm} 100%)`};
          border: 1px solid ${T.hairline};
          padding: 24px;
          position: relative;
          transition: border-color 0.25s, box-shadow 0.3s;
          box-shadow: ${darkMode
            ? `inset 0 1px 0 rgba(242, 237, 224, 0.025)`
            : `inset 0 1px 0 rgba(255,255,255,0.55)`};
        }
        .mep-control-card:focus-within {
          border-color: ${T.accent};
          box-shadow: ${darkMode
            ? `0 0 0 1px ${T.accent}, 0 0 28px ${T.glow}, inset 0 1px 0 rgba(242, 237, 224, 0.04)`
            : `0 0 0 1px ${T.accent}, 0 12px 32px -10px rgba(30, 58, 111, 0.18), inset 0 1px 0 rgba(255,255,255,0.7)`};
        }
        .mep-control-card-corner {
          position: absolute;
          top: 0;
          left: 0;
        }
        .mep-control-card-corner::before,
        .mep-control-card-corner::after {
          content: "";
          position: absolute;
          background: ${T.accent};
        }
        .mep-control-card-corner::before {
          width: 28px;
          height: 1px;
          top: 0;
          left: 0;
        }
        .mep-control-card-corner::after {
          width: 1px;
          height: 28px;
          top: 0;
          left: 0;
        }

        /* ===== ROW PREMIUM (años/especialidades/etc) ===== */
        .mep-prow {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 9px 12px;
          cursor: pointer;
          position: relative;
          border-left: 2px solid transparent;
          transition: background 0.18s, border-left-color 0.18s, padding-left 0.18s;
        }
        .mep-prow:hover {
          background: ${T.paperWarm};
          border-left-color: ${darkMode ? T.hairline : T.hairline};
          padding-left: 14px;
        }
        .mep-prow[data-selected="true"] {
          background: ${darkMode ? T.accentSoft : T.accentSoft};
          border-left-color: ${T.accent};
        }
        .mep-prow[data-selected="true"]:hover {
          background: ${darkMode ? '#1A2742' : T.accentTint};
        }
        .mep-prow[data-selected="true"] .mep-prow-label {
          color: ${T.ink};
          font-weight: 600;
        }

        /* ===== RESULT CARD (búsqueda) ===== */
        .mep-result-card {
          padding: 14px 16px 14px 18px;
          background: ${T.paperSoft};
          border: 1px solid ${T.hairlineSoft};
          border-left: 2px solid ${T.hairline};
          cursor: pointer;
          transition: all 0.22s cubic-bezier(0.22, 1, 0.36, 1);
          position: relative;
        }
        .mep-result-card:hover {
          background: ${T.paperWarm};
          border-color: ${T.hairline};
          border-left-color: ${T.accent};
          transform: translateX(2px);
          box-shadow: ${darkMode
            ? `0 4px 24px -8px rgba(0,0,0,0.4), 0 0 0 1px ${T.hairline}`
            : `0 4px 16px -4px rgba(14, 18, 24, 0.08)`};
        }
        .mep-result-card[data-selected="true"] {
          background: ${darkMode ? T.accentSoft : T.accentSoft};
          border-left: 2px solid ${T.accent};
          border-color: ${darkMode ? `${T.accent}30` : `${T.accent}40`};
          box-shadow: ${darkMode
            ? `0 0 0 1px ${T.accent}40, 0 0 24px ${T.glow}`
            : `0 0 0 1px ${T.accent}30, 0 6px 20px -8px rgba(30, 58, 111, 0.18)`};
        }

        /* ===== SEGMENTED CONTROL ===== */
        .mep-segment {
          display: inline-flex;
          padding: 3px;
          background: ${T.paperSoft};
          border: 1px solid ${T.hairline};
          position: relative;
        }
        .mep-segment-btn {
          padding: 10px 22px;
          font-family: "Manrope", sans-serif;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.02em;
          color: ${T.inkSoft};
          background: transparent;
          border: none;
          cursor: pointer;
          transition: color 0.2s;
          position: relative;
          z-index: 1;
        }
        .mep-segment-btn:hover {
          color: ${T.ink};
        }
        .mep-segment-btn[data-active="true"] {
          color: ${darkMode ? T.paper : T.paper};
          background: ${T.ink};
          font-weight: 600;
        }
        .mep-segment-btn[data-active="true"][data-variant="accent"] {
          background: ${T.accent};
          color: ${darkMode ? '#07090D' : T.paper};
          ${darkMode ? `box-shadow: 0 0 0 1px ${T.accent}, 0 0 16px ${T.glow};` : ''}
        }

        /* ===== CHIP REMOVABLE PREMIUM ===== */
        .mep-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 5px 10px 5px 12px;
          font-family: "Manrope", sans-serif;
          font-size: 11.5px;
          font-weight: 500;
          letter-spacing: 0.01em;
          background: ${T.paperSoft};
          color: ${T.ink};
          border: 1px solid ${T.hairline};
          cursor: pointer;
          transition: all 0.18s;
          line-height: 1;
        }
        .mep-chip:hover {
          border-color: ${T.accent};
          color: ${T.accent};
          background: ${T.accentTint};
          ${darkMode ? `box-shadow: 0 0 0 1px ${T.accent}40, 0 0 14px ${T.glow};` : ''}
        }
        .mep-chip-x {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 13px;
          height: 13px;
          margin-left: 2px;
          opacity: 0.55;
          transition: opacity 0.15s;
        }
        .mep-chip:hover .mep-chip-x { opacity: 1; }
        .mep-chip[data-variant="accent"] {
          background: ${T.accent};
          color: ${darkMode ? '#07090D' : T.paper};
          border-color: ${T.accent};
        }
        .mep-chip[data-variant="accent"]:hover {
          background: ${T.accentDeep};
          color: ${darkMode ? T.ink : T.paper};
        }
        .mep-chip[data-variant="mono"] {
          font-family: "Geist Mono", monospace;
          font-size: 11px;
        }

        /* ===== SLIDER STYLED ===== */
        .mep-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 4px;
          background: ${T.hairlineSoft};
          outline: none;
          cursor: pointer;
          transition: background 0.2s;
        }
        .mep-slider:hover {
          background: ${T.hairline};
        }
        .mep-slider:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .mep-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          background: ${T.accent};
          border: 3px solid ${T.paper};
          border-radius: 50%;
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s;
          box-shadow: ${darkMode
            ? `0 0 0 1px ${T.accent}, 0 0 14px ${T.glow}`
            : `0 2px 8px rgba(30, 58, 111, 0.3)`};
        }
        .mep-slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
          ${darkMode ? `box-shadow: 0 0 0 1px ${T.accent}, 0 0 28px ${T.glow};` : `box-shadow: 0 4px 16px rgba(30, 58, 111, 0.45);`}
        }
        .mep-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          background: ${T.accent};
          border: 3px solid ${T.paper};
          border-radius: 50%;
          cursor: pointer;
          box-shadow: ${darkMode
            ? `0 0 0 1px ${T.accent}, 0 0 14px ${T.glow}`
            : `0 2px 8px rgba(30, 58, 111, 0.3)`};
        }

        /* ===== RESULT GROUP HEADER ===== */
        .mep-area-header {
          position: relative;
        }
        .mep-area-header::before {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: -2px;
          height: 2px;
          background: linear-gradient(90deg, ${T.accent} 0%, ${T.accent}80 25%, transparent 100%);
        }
        .mep-paper-grain::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          opacity: ${darkMode ? '0.07' : '0.025'};
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
          mix-blend-mode: ${darkMode ? 'screen' : 'multiply'};
          z-index: 0;
        }
        ${darkMode ? `
        .mep-paper-grain::after {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(ellipse 90% 50% at 50% -10%, rgba(125, 165, 217, 0.07) 0%, transparent 55%),
            radial-gradient(ellipse 120% 80% at 50% 110%, rgba(125, 165, 217, 0.03) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 15% 30%, rgba(242, 237, 224, 0.02) 0%, transparent 60%);
          z-index: 0;
        }
        ` : ''}
        .mep-rule {
          height: 1px;
          background: ${T.hairline};
          width: 100%;
        }
        .mep-rule-soft {
          height: 1px;
          background: ${T.hairlineSoft};
          width: 100%;
        }
        .mep-cap {
          font-family: "Manrope", sans-serif;
          font-weight: 600;
          font-size: 10px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
        }
        .mep-mono {
          font-family: "Geist Mono", "JetBrains Mono", monospace;
          font-variant-numeric: tabular-nums;
        }
        .mep-serif {
          font-family: "Newsreader", "Instrument Serif", serif;
        }
        .mep-sans {
          font-family: "Manrope", system-ui, sans-serif;
        }
        .mep-input {
          background: ${T.paperSoft};
          border: 1px solid ${T.hairline};
          color: ${T.ink};
          font-family: "Manrope", sans-serif;
          transition: border-color 0.18s, background 0.18s, box-shadow 0.2s;
        }
        .mep-input:focus {
          outline: none;
          border-color: ${T.accent};
          background: ${T.paper};
          ${darkMode ? `box-shadow: 0 0 0 3px ${T.glow}, inset 0 0 0 1px ${T.accent}30;` : ''}
        }
        .mep-input::placeholder {
          color: ${T.inkMute};
        }
        .mep-checkbox {
          appearance: none;
          width: 14px;
          height: 14px;
          border: 1px solid ${T.hairline};
          background: ${T.paperSoft};
          flex-shrink: 0;
          cursor: pointer;
          position: relative;
          transition: all 0.15s;
        }
        .mep-checkbox:checked {
          background: ${T.accent};
          border-color: ${T.accent};
        }
        .mep-checkbox:checked::after {
          content: "";
          position: absolute;
          top: 2px;
          left: 5px;
          width: 3px;
          height: 7px;
          border: solid ${T.paper};
          border-width: 0 1.5px 1.5px 0;
          transform: rotate(45deg);
        }
        .mep-row {
          transition: background 0.16s, color 0.12s, border-color 0.16s;
          border-left: 2px solid transparent;
        }
        .mep-row:hover {
          background: ${T.paperWarm};
          ${darkMode ? `border-left-color: ${T.hairline};` : ''}
        }
        .mep-row[data-selected="true"] {
          background: ${T.accentSoft};
          border-left-color: ${T.accent};
        }
        .mep-row[data-selected="true"]:hover {
          background: ${darkMode ? '#1A2742' : T.accentTint};
        }
        .mep-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border: 1px solid ${T.hairline};
          background: ${T.paperSoft};
          color: ${T.ink};
          font-family: "Manrope", sans-serif;
          font-size: 11px;
          letter-spacing: 0.02em;
          font-weight: 500;
          border-radius: 999px;
          transition: all 0.15s;
        }
        .mep-pill[data-active="true"] {
          background: ${T.accent};
          border-color: ${T.accent};
          color: ${T.paper};
        }
        .mep-pill[data-removable="true"] {
          padding-right: 6px;
          cursor: pointer;
        }
        .mep-pill[data-removable="true"]:hover {
          border-color: ${T.accent};
          color: ${T.accent};
        }
        .mep-section {
          position: relative;
        }
        .mep-section + .mep-section {
          padding-top: 56px;
          margin-top: 56px;
          border-top: 1px solid ${T.hairlineSoft};
        }
        .mep-bar-track {
          height: 2px;
          background: ${T.hairlineSoft};
          width: 100%;
          position: relative;
          overflow: hidden;
        }
        .mep-bar-fill {
          position: absolute;
          inset: 0 auto 0 0;
          background: ${T.accent};
          transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .mep-figure {
          font-family: "Newsreader", serif;
          font-style: italic;
          color: ${T.inkSoft};
        }
        .mep-cta {
          background: ${darkMode
            ? `linear-gradient(180deg, ${T.paperWarm}, ${T.paperSoft})`
            : T.ink};
          color: ${darkMode ? T.ink : T.paper};
          font-family: "Manrope", sans-serif;
          font-weight: 500;
          letter-spacing: 0.02em;
          padding: 14px 32px;
          border: ${darkMode ? `1px solid ${T.hairline}` : 'none'};
          cursor: pointer;
          transition: background 0.25s, color 0.25s, transform 0.1s, box-shadow 0.25s, border-color 0.25s;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          position: relative;
          overflow: hidden;
        }
        .mep-cta:hover {
          background: ${T.accent};
          color: ${darkMode ? '#07090D' : T.paper};
          ${darkMode ? `
            border-color: ${T.accent};
            box-shadow: 0 0 0 1px ${T.accent}, 0 0 24px ${T.glow}, inset 0 0 0 1px rgba(255,255,255,0.08);
          ` : 'box-shadow: 0 6px 24px rgba(30, 58, 111, 0.28);'}
        }
        .mep-cta:active {
          transform: translateY(1px);
        }
        .mep-cta:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .mep-cta:disabled:hover {
          background: ${darkMode
            ? `linear-gradient(180deg, ${T.paperWarm}, ${T.paperSoft})`
            : T.ink};
          color: ${darkMode ? T.ink : T.paper};
          box-shadow: none;
          border-color: ${T.hairline};
        }
        .mep-cta-secondary {
          background: ${darkMode ? T.paperSoft : 'transparent'};
          color: ${T.ink};
          font-family: "Manrope", sans-serif;
          font-size: 12px;
          font-weight: 500;
          padding: 8px 16px;
          border: 1px solid ${T.hairline};
          cursor: pointer;
          transition: all 0.18s;
          letter-spacing: 0.02em;
        }
        .mep-cta-secondary:hover {
          border-color: ${T.accent};
          color: ${T.accent};
          ${darkMode ? `
            background: ${T.accentTint};
            box-shadow: 0 0 0 1px ${T.accent}40, 0 0 16px ${T.glow};
          ` : ''}
        }
        .mep-cta-secondary:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .mep-cta-secondary:disabled:hover {
          border-color: ${T.hairline};
          color: ${T.ink};
          background: ${darkMode ? T.paperSoft : 'transparent'};
          box-shadow: none;
        }
        .mep-cta-ghost {
          background: transparent;
          border: none;
          color: ${T.inkSoft};
          font-family: "Manrope", sans-serif;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.02em;
          cursor: pointer;
          padding: 4px 0;
          transition: color 0.15s;
        }
        .mep-cta-ghost:hover {
          color: ${T.accent};
        }
        .mep-quote {
          font-family: "Newsreader", serif;
          font-style: italic;
          color: ${T.inkSoft};
          font-size: 13px;
          line-height: 1.6;
        }
        .mep-source-tab {
          font-family: "Manrope", sans-serif;
          font-size: 13px;
          padding: 10px 18px;
          background: transparent;
          border: none;
          color: ${T.inkSoft};
          cursor: pointer;
          position: relative;
          transition: color 0.15s;
          letter-spacing: 0.01em;
        }
        .mep-source-tab[data-active="true"] {
          color: ${T.ink};
          font-weight: 600;
        }
        .mep-source-tab[data-active="true"]::after {
          content: "";
          position: absolute;
          bottom: -1px;
          left: 18px;
          right: 18px;
          height: 2px;
          background: ${T.accent};
        }
        .mep-source-tab:hover { color: ${T.ink}; }
        .mep-search-input {
          background: transparent;
          border: none;
          width: 100%;
          font-family: "Newsreader", serif;
          font-size: 28px;
          line-height: 1.2;
          color: ${T.ink};
          padding: 12px 0;
          letter-spacing: -0.01em;
          ${darkMode ? `text-shadow: 0 0 38px ${T.glow};` : ''}
        }
        .mep-search-input:focus {
          outline: none;
        }
        .mep-search-input::placeholder {
          color: ${T.inkMute};
          font-style: italic;
          ${darkMode ? 'text-shadow: none;' : ''}
        }
        .mep-id-stamp {
          font-family: "Geist Mono", monospace;
          font-size: 11px;
          color: ${T.accent};
          font-weight: 500;
          letter-spacing: 0.04em;
          ${darkMode ? `text-shadow: 0 0 14px rgba(125, 165, 217, 0.4);` : ''}
        }
        .mep-match-tag {
          font-family: "Geist Mono", monospace;
          font-size: 9px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: ${T.inkSoft};
          background: ${darkMode ? T.paperWarm : T.paperSoft};
          padding: 2px 6px;
          border: 1px solid ${T.hairlineSoft};
        }
        @keyframes mep-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .mep-cursor::after {
          content: "▋";
          color: ${T.accent};
          animation: mep-blink 1.2s infinite;
          margin-left: 4px;
        }
      `}</style>

      {/* FLOATING BADGE 'Selección / N preguntas disponibles' removido
          a pedido del usuario — estorbaba en mobile y duplica info que
          ya está visible en la sección de filtros. */}

      {/* ===== CONTENEDOR PRINCIPAL — vellum / paper ===== */}
      <div
        className="min-h-screen mep-paper-grain relative"
        style={{
          background: T.paper,
          color: T.ink,
          fontFamily: '"Manrope", system-ui, sans-serif',
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 pt-10 pb-24 relative">

          {/* ============================================================
              NAVEGACIÓN — minimal hairline bar
              ============================================================ */}
          <motion.div
            {...sectionFade(0)}
            className="flex items-center justify-between mb-12 pb-5"
            style={{ borderBottom: `1px solid ${T.hairline}` }}
          >
            <div className="flex items-center gap-5">
              {onGoHome && (
                <button
                  onClick={onGoHome}
                  className="flex items-center gap-2 group"
                  style={{ color: T.inkSoft, fontFamily: '"Manrope", sans-serif' }}
                >
                  <Home size={14} />
                  <span className="text-sm tracking-wide group-hover:underline" style={{ color: T.ink }}>
                    Inicio
                  </span>
                </button>
              )}
              {onBack && (
                <>
                  <span style={{ color: T.hairline }}>·</span>
                  <button
                    onClick={onBack}
                    className="flex items-center gap-2 group"
                    style={{ color: T.inkSoft, fontFamily: '"Manrope", sans-serif' }}
                  >
                    <ChevronLeft size={14} />
                    <span className="text-sm tracking-wide group-hover:underline" style={{ color: T.ink }}>
                      Regresar
                    </span>
                  </button>
                </>
              )}
            </div>
            {/* Indicador en vivo — pulse de archivo activo */}
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{
                  background: T.accent,
                  boxShadow: darkMode
                    ? `0 0 14px ${T.accent}, 0 0 4px ${T.accent}`
                    : `0 0 8px ${T.accent}80`,
                  animation: 'mep-pulse 2.4s ease-in-out infinite',
                }}
              />
              <span className="mep-cap" style={{ color: T.inkSoft }}>
                <span className="mep-mono" style={{ color: T.ink, fontWeight: 500 }}>
                  {sourceQuestions.length.toLocaleString()}
                </span>{' '}
                en archivo
              </span>
            </div>
          </motion.div>

          {/* ============================================================
              HERO EDITORIAL — composición tipo magazine cover
              ============================================================ */}
          <motion.header
            {...sectionFade(0.05)}
            className="mb-20 relative"
          >
            {/* Pequeña etiqueta superior tipo "edition mark" */}
            <div
              className="flex items-center gap-3 mb-7"
              style={{ color: T.inkMute }}
            >
              <div
                className="h-px flex-shrink-0"
                style={{ width: '40px', background: T.accent }}
              />
              <span className="mep-cap mep-mono" style={{ color: T.accent, letterSpacing: '0.32em' }}>
                Archivo · 2010 — 2025
              </span>
              <div
                className="h-px flex-1"
                style={{ background: T.hairlineSoft }}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              {/* Headline gigante — ocupa 8 columnas */}
              <div className="lg:col-span-8">
                <h1
                  className="leading-[0.92] tracking-[-0.025em]"
                  style={{
                    fontFamily: '"Newsreader", "Instrument Serif", serif',
                    fontWeight: 400,
                    fontSize: 'clamp(3rem, 7.5vw, 6.5rem)',
                    color: T.ink,
                    textShadow: darkMode ? `0 0 60px rgba(242, 237, 224, 0.04)` : 'none',
                  }}
                >
                  Construye{' '}
                  <em
                    style={{
                      fontStyle: 'italic',
                      color: T.accent,
                      textShadow: darkMode ? `0 0 36px ${T.glow}` : 'none',
                    }}
                  >
                    el examen
                  </em>
                  {' '}que tu mente necesita esta semana.
                </h1>

                <div
                  className="mt-8 max-w-xl pl-5"
                  style={{ borderLeft: `2px solid ${T.accent}` }}
                >
                  <p
                    className="text-[1.05rem] leading-[1.55]"
                    style={{
                      color: T.inkSoft,
                      fontFamily: '"Newsreader", serif',
                      fontStyle: 'italic',
                    }}
                  >
                    Una palabra a través del archivo es suficiente — la búsqueda transversal
                    atraviesa áreas, temas y subtemas para que ninguna pregunta relevante
                    quede oculta por la jerarquía.
                  </p>
                </div>
              </div>

              {/* Stat block dramático — 4 columnas */}
              <div className="lg:col-span-4">
                <div
                  className="relative px-6 py-7"
                  style={{
                    background: darkMode
                      ? `linear-gradient(155deg, ${T.paperSoft} 0%, ${T.paper} 100%)`
                      : `linear-gradient(155deg, ${T.paperSoft} 0%, ${T.paperWarm} 100%)`,
                    border: `1px solid ${T.hairline}`,
                    boxShadow: darkMode
                      ? `inset 0 1px 0 rgba(242, 237, 224, 0.04), 0 24px 60px -20px rgba(0,0,0,0.7)`
                      : `inset 0 1px 0 rgba(255,255,255,0.7), 0 16px 40px -16px rgba(14,18,24,0.18)`,
                  }}
                >
                  {/* Esquina superior — accent corner */}
                  <div
                    className="absolute top-0 left-0 w-12 h-px"
                    style={{ background: T.accent }}
                  />
                  <div
                    className="absolute top-0 left-0 w-px h-12"
                    style={{ background: T.accent }}
                  />

                  <div className="space-y-1 mb-5">
                    <span className="mep-cap" style={{ color: T.inkMute, letterSpacing: '0.24em' }}>
                      Archivo en uso
                    </span>
                  </div>

                  {/* Número hero */}
                  <div className="flex items-baseline gap-2 mb-1">
                    <span
                      className="mep-mono leading-none"
                      style={{
                        fontSize: 'clamp(3.5rem, 7vw, 5rem)',
                        color: T.ink,
                        fontWeight: 400,
                        letterSpacing: '-0.04em',
                        textShadow: darkMode
                          ? `0 0 40px rgba(242, 237, 224, 0.08), 0 0 80px ${T.glow}`
                          : 'none',
                      }}
                    >
                      {sourceQuestions.length.toLocaleString()}
                    </span>
                  </div>
                  <p
                    className="mep-figure mb-6"
                    style={{ color: T.inkSoft, fontSize: '14px' }}
                  >
                    preguntas reales del residentado
                  </p>

                  {/* Sub-stats en grid 2-col */}
                  <div
                    className="grid grid-cols-2 gap-x-4 pt-4"
                    style={{ borderTop: `1px solid ${T.hairlineSoft}` }}
                  >
                    <div>
                      <span
                        className="mep-mono text-2xl block leading-tight"
                        style={{ color: T.ink, fontWeight: 500 }}
                      >
                        {uniqueAreas.length}
                      </span>
                      <span className="mep-cap" style={{ color: T.inkMute, letterSpacing: '0.18em' }}>
                        áreas
                      </span>
                    </div>
                    <div>
                      <span
                        className="mep-mono text-2xl block leading-tight"
                        style={{ color: T.ink, fontWeight: 500 }}
                      >
                        {uniqueYears.length}
                      </span>
                      <span className="mep-cap" style={{ color: T.inkMute, letterSpacing: '0.18em' }}>
                        años
                      </span>
                    </div>
                  </div>
                </div>

                {/* Botón global "Limpiar todos los filtros" — debajo del stat card */}
                <AnimatePresence>
                  {totalFiltersCount > 0 && (
                    <motion.button
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                      onClick={resetFilters}
                      className="mt-3 w-full flex items-center justify-between px-4 py-2.5 group"
                      style={{
                        background: 'transparent',
                        border: `1px solid ${T.hairline}`,
                        borderLeft: `2px solid ${T.accent}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = darkMode ? T.accentTint : T.accentSoft;
                        e.currentTarget.style.borderColor = T.accent;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = T.hairline;
                        e.currentTarget.style.borderLeftColor = T.accent;
                      }}
                    >
                      <span className="flex items-baseline gap-2">
                        <span
                          className="mep-mono text-sm"
                          style={{ color: T.accent, fontWeight: 500 }}
                        >
                          {totalFiltersCount}
                        </span>
                        <span
                          className="mep-cap"
                          style={{ color: T.ink, letterSpacing: '0.16em' }}
                        >
                          filtros · limpiar todo
                        </span>
                      </span>
                      <X size={13} style={{ color: T.accent }} />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.header>

          {/* ============================================================
              § 01 · FUENTE — layout asimétrico: meta izq + cards der
              ============================================================ */}
          <motion.section {...sectionFade(0.1)} className="mep-section">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

              {/* COLUMNA IZQUIERDA — meta-info */}
              <div className="lg:col-span-5 xl:col-span-4">
                <div className="flex items-baseline gap-4 mb-3">
                  <span
                    className="mep-mono text-[10px] tracking-[0.28em]"
                    style={{ color: T.inkMute }}
                  >
                    § 01
                  </span>
                  <h2
                    style={{
                      fontFamily: '"Newsreader", serif',
                      fontSize: '1.875rem',
                      fontWeight: 400,
                      color: T.ink,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.05,
                    }}
                  >
                    Fuente
                  </h2>
                </div>
                <p className="text-sm mep-sans mb-5" style={{ color: T.inkSoft, lineHeight: 1.55 }}>
                  Procedencia del banco. La selección activa determina qué archivos atraviesa la búsqueda transversal del § 03.
                </p>

                {/* Status pill mostrando la fuente activa */}
                <div
                  className="inline-flex items-center gap-3 pl-4 pr-5 py-2.5"
                  style={{
                    background: T.paperSoft,
                    border: `1px solid ${T.hairline}`,
                    borderLeft: `2px solid ${T.accent}`,
                  }}
                >
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{
                      background: T.accent,
                      boxShadow: darkMode ? `0 0 8px ${T.accent}` : 'none',
                    }}
                  />
                  <div className="flex items-baseline gap-2">
                    <span className="mep-cap" style={{ color: T.inkMute, letterSpacing: '0.22em' }}>
                      activa
                    </span>
                    <span
                      style={{
                        fontFamily: '"Newsreader", serif',
                        fontStyle: 'italic',
                        fontSize: '15px',
                        color: T.ink,
                      }}
                    >
                      {questionSource === 'simulacro' ? 'simulacro' : 'reales'}
                    </span>
                    <span className="mep-mono text-[11px]" style={{ color: T.accent, fontWeight: 500 }}>
                      · {sourceQuestions.length.toLocaleString()}
                    </span>
                  </div>
                </div>

                {(loadingPredictions || loadingSimulacros) && (
                  <p className="mep-cap mt-4 flex items-center gap-2" style={{ color: T.inkSoft }}>
                    <RefreshCw size={11} className="animate-spin" />
                    cargando fuente
                  </p>
                )}
              </div>

              {/* COLUMNA DERECHA — cards compactas (Reales + Simulacros) */}
              <div className="lg:col-span-7 xl:col-span-8">
                <div
                  className="grid grid-cols-2 gap-2"
                  style={{ gridAutoRows: '1fr' }}
                >
                  {[
                    {
                      id: 'real',
                      label: 'Reales',
                      desc: 'RM 2010-2025',
                      count: allQuestions.length,
                      numeral: '01',
                    },
                    {
                      id: 'simulacro',
                      label: 'Simulacros',
                      desc: 'sets curados',
                      count: availableSimulacros.length,
                      numeral: '02',
                    },
                  ].map((s) => {
                    const isActive = questionSource === s.id;
                    const isLoading = s.id === 'simulacro' && loadingSimulacros;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setQuestionSource(s.id)}
                        disabled={isLoading}
                        className="mep-phoenix text-left group flex flex-col"
                        style={{
                          padding: '8px 11px',
                          minHeight: '64px',
                          background: isActive
                            ? (darkMode
                                ? `linear-gradient(160deg, ${T.paperLift} 0%, ${T.paperSoft} 100%)`
                                : `linear-gradient(160deg, ${T.paperSoft} 0%, ${T.paperWarm} 100%)`)
                            : T.paperSoft,
                          border: `1px solid ${isActive ? T.accent : T.hairline}`,
                          boxShadow: isActive
                            ? (darkMode
                                ? `0 0 0 1px ${T.accent}, inset 0 1px 0 rgba(242, 237, 224, 0.04), 0 12px 28px -10px rgba(0,0,0,0.55), 0 0 22px ${T.glow}`
                                : `0 0 0 1px ${T.accent}, 0 8px 22px -8px rgba(30, 58, 111, 0.20), inset 0 1px 0 rgba(255,255,255,0.7)`)
                            : (darkMode
                                ? `inset 0 1px 0 rgba(242, 237, 224, 0.025)`
                                : `inset 0 1px 0 rgba(255,255,255,0.5)`),
                          cursor: isLoading ? 'wait' : 'pointer',
                          opacity: isLoading ? 0.6 : 1,
                          transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.3s, background 0.3s',
                        }}
                      >
                        {/* Corner accent para active */}
                        {isActive && (
                          <>
                            <div
                              className="absolute top-0 left-0 w-4 h-px"
                              style={{ background: T.accent }}
                            />
                            <div
                              className="absolute top-0 left-0 w-px h-4"
                              style={{ background: T.accent }}
                            />
                          </>
                        )}

                        {/* Top row: numeral + status dot — altura fija */}
                        <div
                          className="flex items-center justify-between"
                          style={{ height: '11px', marginBottom: '4px' }}
                        >
                          <span
                            className="mep-mono text-[8px] tracking-[0.22em]"
                            style={{ color: isActive ? T.accent : T.inkMute }}
                          >
                            {s.numeral}
                          </span>
                          {isLoading ? (
                            <RefreshCw size={7} className="animate-spin" style={{ color: T.inkMute }} />
                          ) : isActive ? (
                            <span
                              className="inline-block w-[3px] h-[3px] rounded-full"
                              style={{
                                background: T.accent,
                                boxShadow: darkMode ? `0 0 6px ${T.accent}` : 'none',
                              }}
                            />
                          ) : (
                            <span style={{ width: '3px', height: '3px' }} />
                          )}
                        </div>

                        {/* Label + count — altura fija con line-height controlado */}
                        <div
                          className="flex items-baseline gap-2"
                          style={{ height: '17px' }}
                        >
                          <h3
                            className="truncate"
                            style={{
                              fontFamily: '"Newsreader", serif',
                              fontSize: '1rem',
                              fontWeight: 400,
                              lineHeight: '17px',
                              color: T.ink,
                              fontStyle: isActive ? 'italic' : 'normal',
                              letterSpacing: '-0.005em',
                              flex: '0 1 auto',
                              minWidth: 0,
                            }}
                          >
                            {s.label}
                          </h3>
                          <span
                            className="mep-mono text-[10px] ml-auto whitespace-nowrap"
                            style={{
                              color: isActive ? T.accent : T.inkSoft,
                              fontWeight: 500,
                              lineHeight: '17px',
                              textShadow: isActive && darkMode ? `0 0 8px ${T.glow}` : 'none',
                            }}
                          >
                            {s.count.toLocaleString()}
                          </span>
                        </div>

                        {/* Desc — altura fija una línea */}
                        <p
                          className="text-[9.5px] truncate"
                          style={{
                            color: T.inkMute,
                            fontFamily: '"Manrope", sans-serif',
                            letterSpacing: '0.02em',
                            lineHeight: '12px',
                            marginTop: '1px',
                          }}
                        >
                          {s.desc}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* Simulacro selector — full width below */}
            {questionSource === 'simulacro' && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 flex items-center gap-4 flex-wrap px-4 py-3"
                style={{
                  background: T.accentTint,
                  border: `1px solid ${T.hairline}`,
                  borderLeft: `2px solid ${T.accent}`,
                }}
              >
                <span className="mep-cap" style={{ color: T.accent, letterSpacing: '0.24em' }}>
                  ⟢ set
                </span>
                <select
                  value={selectedSimulacroId || ''}
                  onChange={(e) => setSelectedSimulacroId(e.target.value || null)}
                  className="mep-input px-3 py-1.5 text-[13px] min-w-[260px]"
                  style={{ borderRadius: 0 }}
                >
                  <option value="">— selecciona un set —</option>
                  {availableSimulacros.map((sim) => (
                    <option key={sim.id} value={sim.id}>
                      {sim.name} ({sim.source})
                    </option>
                  ))}
                </select>
                {selectedSimulacroId && simulacroQuestions.length > 0 && (
                  <span className="mep-mono text-[11px]" style={{ color: T.inkSoft }}>
                    ↳ {simulacroQuestions.length} preguntas
                  </span>
                )}
              </motion.div>
            )}
          </motion.section>

          {/* ============================================================
              § 02 · RANGO Y DOMINIO — tres control cards premium
              ============================================================ */}
          <motion.section {...sectionFade(0.15)} className="mep-section">
            <SectionMarker
              number="02"
              title="Rango y dominio"
              subtitle="Año del examen, especialidad y área anatómica"
              hint={
                <span className="inline-flex items-center gap-2">
                  <span>{rangeFiltersCount} activos</span>
                  {rangeFiltersCount > 0 && (
                    <>
                      <span style={{ color: T.hairline }}>·</span>
                      <button
                        onClick={clearRangeFilters}
                        className="mep-cta-ghost"
                        style={{
                          color: T.accent,
                          fontSize: '10px',
                          letterSpacing: '0.18em',
                          textTransform: 'uppercase',
                        }}
                      >
                        limpiar →
                      </button>
                    </>
                  )}
                </span>
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* AÑOS */}
              <div className="mep-control-card">
                <div className="mep-control-card-corner" />
                <div className="flex items-baseline justify-between mb-1">
                  <h3
                    style={{
                      fontFamily: '"Newsreader", serif',
                      fontSize: '1.5rem',
                      fontWeight: 400,
                      color: T.ink,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Año
                  </h3>
                  <span className="mep-mono text-[10px]" style={{ color: T.inkMute, letterSpacing: '0.18em' }}>
                    {selectedYears.length}/{uniqueYears.length}
                  </span>
                </div>
                <p className="text-xs mep-sans mb-4" style={{ color: T.inkSoft }}>
                  edición del residentado médico
                </p>
                <div className="flex items-center gap-2 mb-3 pb-3" style={{ borderBottom: `1px solid ${T.hairlineSoft}` }}>
                  <button
                    onClick={selectAllYears}
                    className="mep-chip"
                    style={{ padding: '4px 10px', fontSize: '10.5px' }}
                  >
                    todos
                  </button>
                  <button
                    onClick={selectLastFiveYears}
                    className="mep-chip"
                    style={{ padding: '4px 10px', fontSize: '10.5px' }}
                  >
                    2020-24
                  </button>
                  <button
                    onClick={clearYears}
                    className="ml-auto"
                    style={{
                      color: T.inkMute,
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: '10.5px',
                      letterSpacing: '0.05em',
                      cursor: 'pointer',
                      background: 'transparent',
                      border: 'none',
                    }}
                  >
                    limpiar
                  </button>
                </div>
                <div className="space-y-px max-h-72 overflow-y-auto pr-1 -ml-3">
                  {uniqueYears.map((year) => {
                    const sel = selectedYears.includes(year);
                    const count = sourceQuestions.filter((q) => q.year === year).length;
                    return (
                      <label
                        key={`y-${year}`}
                        data-selected={sel}
                        className="mep-prow"
                      >
                        <input
                          type="checkbox"
                          className="mep-checkbox"
                          checked={sel}
                          onChange={() => toggleSelected(year, selectedYears, setSelectedYears)}
                        />
                        <span className="mep-prow-label mep-mono text-sm flex-1" style={{ color: T.ink }}>
                          {year}
                        </span>
                        <span className="mep-mono text-[11px]" style={{ color: sel ? T.accent : T.inkMute }}>
                          {count.toLocaleString()}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* ESPECIALIDADES */}
              <div className="mep-control-card">
                <div className="mep-control-card-corner" />
                <div className="flex items-baseline justify-between mb-1">
                  <h3
                    style={{
                      fontFamily: '"Newsreader", serif',
                      fontSize: '1.5rem',
                      fontWeight: 400,
                      color: T.ink,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Especialidad
                  </h3>
                  <span className="mep-mono text-[10px]" style={{ color: T.inkMute, letterSpacing: '0.18em' }}>
                    {selectedEspecialidades.length}/{uniqueEspecialidades.length}
                  </span>
                </div>
                <p className="text-xs mep-sans mb-4" style={{ color: T.inkSoft }}>
                  rama de la medicina
                </p>
                <div className="flex items-center gap-2 mb-3 pb-3" style={{ borderBottom: `1px solid ${T.hairlineSoft}` }}>
                  <button
                    onClick={selectAllSpecialties}
                    className="mep-chip"
                    style={{ padding: '4px 10px', fontSize: '10.5px' }}
                  >
                    todas
                  </button>
                  <button
                    onClick={clearSpecialties}
                    className="ml-auto"
                    style={{
                      color: T.inkMute,
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: '10.5px',
                      letterSpacing: '0.05em',
                      cursor: 'pointer',
                      background: 'transparent',
                      border: 'none',
                    }}
                  >
                    limpiar
                  </button>
                </div>
                <div className="space-y-px max-h-72 overflow-y-auto pr-1 -ml-3">
                  {uniqueEspecialidades.map((esp) => {
                    const sel = selectedEspecialidades.includes(esp);
                    return (
                      <label
                        key={`e-${esp}`}
                        data-selected={sel}
                        className="mep-prow"
                      >
                        <input
                          type="checkbox"
                          className="mep-checkbox"
                          checked={sel}
                          onChange={() => toggleSelected(esp, selectedEspecialidades, setSelectedEspecialidades)}
                        />
                        <span className="mep-prow-label text-sm flex-1 mep-sans" style={{ color: T.ink }}>
                          {esp}
                        </span>
                      </label>
                    );
                  })}
                  {uniqueEspecialidades.length === 0 && (
                    <p className="mep-figure text-xs px-3 py-2">no hay especialidades en esta fuente</p>
                  )}
                </div>
              </div>

              {/* ÁREAS */}
              <div className="mep-control-card">
                <div className="mep-control-card-corner" />
                <div className="flex items-baseline justify-between mb-1">
                  <h3
                    style={{
                      fontFamily: '"Newsreader", serif',
                      fontSize: '1.5rem',
                      fontWeight: 400,
                      color: T.ink,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Área
                  </h3>
                  <span className="mep-mono text-[10px]" style={{ color: T.inkMute, letterSpacing: '0.18em' }}>
                    {selectedAreas.length}/{uniqueAreas.length}
                  </span>
                </div>
                <p className="text-xs mep-sans mb-4" style={{ color: T.inkSoft }}>
                  dominio anatómico-funcional
                </p>
                <div className="flex items-center gap-2 mb-3 pb-3" style={{ borderBottom: `1px solid ${T.hairlineSoft}` }}>
                  <button
                    onClick={selectAllAreas}
                    className="mep-chip"
                    style={{ padding: '4px 10px', fontSize: '10.5px' }}
                  >
                    todas
                  </button>
                  <button
                    onClick={clearAreas}
                    className="ml-auto"
                    style={{
                      color: T.inkMute,
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: '10.5px',
                      letterSpacing: '0.05em',
                      cursor: 'pointer',
                      background: 'transparent',
                      border: 'none',
                    }}
                  >
                    limpiar
                  </button>
                </div>
                <div className="space-y-px max-h-72 overflow-y-auto pr-1 -ml-3">
                  {uniqueAreas.map((area) => {
                    const sel = selectedAreas.includes(area);
                    const count = filteredBase.filter((q) => q.area === area).length;
                    return (
                      <label
                        key={`a-${area}`}
                        data-selected={sel}
                        className="mep-prow"
                      >
                        <input
                          type="checkbox"
                          className="mep-checkbox"
                          checked={sel}
                          onChange={() => toggleSelected(area, selectedAreas, setSelectedAreas)}
                        />
                        <span className="mep-prow-label text-sm flex-1 mep-sans" style={{ color: T.ink }}>
                          {area}
                        </span>
                        <span className="mep-mono text-[11px]" style={{ color: sel ? T.accent : T.inkMute }}>
                          {count.toLocaleString()}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

            </div>
          </motion.section>

          {/* ============================================================
              § 03 · BÚSQUEDA TRANSVERSAL — pieza central
              ============================================================ */}
          <motion.section {...sectionFade(0.2)} className="mep-section" id="transversal-search">
            <SectionMarker
              number="03"
              title="Búsqueda transversal"
              subtitle="Atraviesa áreas, temas y subtemas — encuentra todo lo relacionado"
              hint={isSearching ? 'buscando…' : (globalSearch ? `${globalSearchResults.length} coincidencias` : '')}
              accent
            />

            {/* Campo de búsqueda grande */}
            <div
              className="relative mb-2"
              style={{ borderBottom: `2px solid ${globalSearch ? T.accent : T.hairline}`, transition: 'border-color 0.2s' }}
            >
              <div className="flex items-center gap-4">
                <Search size={22} style={{ color: globalSearch ? T.accent : T.inkMute, flexShrink: 0 }} />
                <input
                  type="text"
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  placeholder="meningitis, hiperkalemia, sepsis neonatal, ECG…"
                  className="mep-search-input"
                  spellCheck="false"
                  autoComplete="off"
                />
                {globalSearch && (
                  <button
                    onClick={() => setGlobalSearch('')}
                    className="flex-shrink-0 p-2 transition-colors"
                    style={{ color: T.inkMute }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = T.accent)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = T.inkMute)}
                    title="Limpiar"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>

            {/* Filtros activos del panel principal aplicados a la búsqueda */}
            <div className="flex items-center gap-3 flex-wrap mt-4 mb-6">
              <span className="mep-cap" style={{ color: T.inkMute }}>aplicando</span>
              {selectedYears.length > 0 ? (
                selectedYears.map((y) => (
                  <span key={`af-y-${y}`} className="mep-pill mep-mono">{y}</span>
                ))
              ) : (
                <span className="mep-pill" style={{ color: T.inkSoft }}>todos los años</span>
              )}
              {selectedAreas.length > 0 && selectedAreas.map((a) => (
                <span key={`af-a-${a}`} className="mep-pill">{a}</span>
              ))}
              {selectedEspecialidades.length > 0 && selectedEspecialidades.map((e) => (
                <span key={`af-e-${e}`} className="mep-pill">{e}</span>
              ))}
              {!globalSearch && (
                <span className="mep-figure ml-auto text-xs">
                  escribe para explorar el archivo
                </span>
              )}
            </div>

            {/* RESULTADOS AGRUPADOS POR JERARQUÍA */}
            {globalSearch && (
              <div className="mt-2">
                {globalSearchResults.length === 0 && !isSearching ? (
                  <div className="py-12 text-center">
                    <p
                      className="mep-figure text-base mb-2"
                      style={{ color: T.inkSoft }}
                    >
                      Sin coincidencias para <em style={{ color: T.ink }}>"{globalSearch}"</em>
                    </p>
                    <p className="mep-cap" style={{ color: T.inkMute }}>
                      Prueba con otro término o ajusta los filtros del § 02
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Acción bar superior */}
                    <div
                      className="flex items-center justify-between mb-5 py-3"
                      style={{ borderTop: `1px solid ${T.hairlineSoft}`, borderBottom: `1px solid ${T.hairlineSoft}` }}
                    >
                      <div className="flex items-baseline gap-3">
                        <span
                          className="mep-mono text-2xl"
                          style={{ color: T.accent, fontWeight: 500 }}
                        >
                          {globalSearchResults.length}
                        </span>
                        <span className="mep-figure text-sm">
                          {globalSearchResults.length === 1 ? 'coincidencia' : 'coincidencias'}{' '}
                          en {groupedSearchResults.length}{' '}
                          {groupedSearchResults.length === 1 ? 'área' : 'áreas'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        {searchAlreadyInDirect > 0 && (
                          <span className="mep-cap" style={{ color: T.sage }}>
                            {searchAlreadyInDirect} ya en selección
                          </span>
                        )}
                        <button
                          onClick={addAllSearchToDirect}
                          className="mep-cta-secondary"
                          disabled={searchAlreadyInDirect === globalSearchResults.length}
                        >
                          {searchAlreadyInDirect === globalSearchResults.length
                            ? 'todas seleccionadas'
                            : `agregar las ${globalSearchResults.length} al examen →`}
                        </button>
                      </div>
                    </div>

                    {/* Grupos jerárquicos premium */}
                    <div className="space-y-10 max-h-[44rem] overflow-y-auto pr-2 -mr-2">
                      {groupedSearchResults.map((areaGroup, areaIdx) => (
                        <motion.div
                          key={`grp-${areaGroup.area}`}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: areaIdx * 0.06 }}
                        >
                          {/* Área header — gran tipografía editorial */}
                          <div className="mep-area-header pb-3 mb-5 flex items-end justify-between">
                            <div className="flex items-baseline gap-4">
                              <span
                                className="mep-mono text-[11px]"
                                style={{ color: T.accent, letterSpacing: '0.32em' }}
                              >
                                ⟢ {String(areaIdx + 1).padStart(2, '0')}
                              </span>
                              <h3
                                style={{
                                  fontFamily: '"Newsreader", serif',
                                  fontSize: '1.625rem',
                                  fontWeight: 400,
                                  color: T.ink,
                                  letterSpacing: '-0.015em',
                                  textShadow: darkMode ? `0 0 18px ${T.glow}` : 'none',
                                }}
                              >
                                {areaGroup.area}
                              </h3>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                              <span
                                className="mep-mono text-2xl"
                                style={{ color: T.accent, fontWeight: 500 }}
                              >
                                {areaGroup.count}
                              </span>
                              <span className="mep-cap" style={{ color: T.inkMute }}>
                                {areaGroup.count === 1 ? 'preg.' : 'pregs.'}
                              </span>
                            </div>
                          </div>

                          {/* Topics dentro del área */}
                          {areaGroup.topics.map((topicGroup) => (
                            <div
                              key={`tg-${areaGroup.area}-${topicGroup.topic}`}
                              className="mb-6 last:mb-0"
                            >
                              {/* Topic › subtopic breadcrumb editorial */}
                              <div className="flex items-baseline gap-2 mb-3 pb-2" style={{ borderBottom: `1px dashed ${T.hairlineSoft}` }}>
                                <span
                                  style={{
                                    color: T.ink,
                                    fontFamily: '"Newsreader", serif',
                                    fontSize: '15px',
                                    fontStyle: 'italic',
                                    fontWeight: 400,
                                  }}
                                >
                                  {topicGroup.topic}
                                </span>
                                {topicGroup.subtopics.length > 0 &&
                                  topicGroup.subtopics.some((s) => s.subtopic !== 'Sin subtema') && (
                                  <>
                                    <span style={{ color: T.hairline }}>›</span>
                                    <span
                                      className="mep-cap"
                                      style={{ color: T.inkSoft, letterSpacing: '0.16em' }}
                                    >
                                      {topicGroup.subtopics
                                        .map((s) => s.subtopic)
                                        .filter((s) => s !== 'Sin subtema')
                                        .join(' · ')}
                                    </span>
                                  </>
                                )}
                                <span className="mep-mono text-[10px] ml-auto" style={{ color: T.inkMute }}>
                                  {topicGroup.count}
                                </span>
                              </div>

                              {/* Preguntas como cards */}
                              <div className="grid grid-cols-1 gap-2">
                                {topicGroup.subtopics
                                  .flatMap((sg) => sg.questions)
                                  .map((q) => {
                                    const isInDirect = directIdSet.has(q.id);
                                    const matchTags = (q.matchedIn || '')
                                      .split(',')
                                      .map((s) => s.trim())
                                      .filter(Boolean);
                                    const snippet = q.case_description || q.question_text || '';
                                    const trimmed =
                                      snippet.length > 200 ? snippet.slice(0, 200) + '…' : snippet;

                                    return (
                                      <div
                                        key={`q-${q.id}`}
                                        data-selected={isInDirect}
                                        className="mep-result-card"
                                        onClick={() => toggleDirectSelection(q.id)}
                                      >
                                        {/* Línea superior: ID, year, subtopic specific, checkbox */}
                                        <div className="flex items-start gap-3">
                                          <input
                                            type="checkbox"
                                            className="mep-checkbox mt-1"
                                            checked={isInDirect}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              toggleDirectSelection(q.id);
                                            }}
                                          />
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline gap-3 flex-wrap mb-1.5">
                                              <span className="mep-id-stamp">
                                                #
                                                {typeof q.id === 'string'
                                                  ? q.id
                                                  : q.id.toString().padStart(5, '0')}
                                              </span>
                                              {q.year && (
                                                <span
                                                  className="mep-mono text-[10px] px-1.5 py-0.5"
                                                  style={{
                                                    color: T.inkMute,
                                                    background: T.paperWarm,
                                                    border: `1px solid ${T.hairlineSoft}`,
                                                  }}
                                                >
                                                  {q.year}
                                                </span>
                                              )}
                                              {q.subtopic_specific && (
                                                <span
                                                  className="text-xs"
                                                  style={{
                                                    color: T.ink,
                                                    fontFamily: '"Manrope", sans-serif',
                                                    fontWeight: 600,
                                                    letterSpacing: '0.005em',
                                                  }}
                                                >
                                                  {q.subtopic_specific}
                                                </span>
                                              )}
                                              {isInDirect && (
                                                <span
                                                  className="mep-cap text-[9px] ml-auto"
                                                  style={{ color: T.accent, letterSpacing: '0.24em' }}
                                                >
                                                  ● seleccionada
                                                </span>
                                              )}
                                            </div>
                                            {trimmed && (
                                              <p
                                                className="text-[13.5px] leading-relaxed"
                                                style={{
                                                  color: T.inkSoft,
                                                  fontFamily: '"Newsreader", serif',
                                                  fontStyle: 'italic',
                                                }}
                                              >
                                                <span style={{ color: T.accent, marginRight: '4px' }}>"</span>
                                                {trimmed}
                                                <span style={{ color: T.accent, marginLeft: '2px' }}>"</span>
                                              </p>
                                            )}
                                            {matchTags.length > 0 && (
                                              <div className="flex flex-wrap gap-1.5 mt-2.5">
                                                {matchTags.map((tag) => (
                                                  <span
                                                    key={`tag-${q.id}-${tag}`}
                                                    className="mep-match-tag"
                                                  >
                                                    ↳ {tag}
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          ))}
                        </motion.div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Banner de selección directa activa */}
            {directCount > 0 && (
              <div
                className="mt-6 px-5 py-4 flex items-center justify-between flex-wrap gap-3 relative overflow-hidden"
                style={{
                  background: darkMode
                    ? `linear-gradient(135deg, ${T.accentSoft} 0%, ${T.accentTint} 100%)`
                    : T.accentSoft,
                  borderLeft: `3px solid ${T.accent}`,
                  boxShadow: darkMode
                    ? `inset 0 0 0 1px ${T.accent}25, 0 0 24px ${T.glow}`
                    : 'none',
                }}
              >
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span
                    className="mep-mono text-xl"
                    style={{
                      color: darkMode ? T.accent : T.accentDeep,
                      fontWeight: 500,
                      textShadow: darkMode ? `0 0 16px ${T.glow}` : 'none',
                    }}
                  >
                    {directCount}
                  </span>
                  <span className="mep-figure text-sm" style={{ color: T.ink }}>
                    {directCount === 1 ? 'pregunta seleccionada directamente' : 'preguntas seleccionadas directamente'} —{' '}
                    el examen usará exactamente estas
                  </span>
                </div>
                <button
                  onClick={clearDirectSelection}
                  className="mep-cta-ghost"
                  style={{ color: darkMode ? T.accent : T.accentDeep }}
                >
                  limpiar selección directa
                </button>
              </div>
            )}
          </motion.section>

          {/* ============================================================
              § 04 · JERARQUÍA CLÁSICA — temas / subtemas / específicos
              (Conservada para flujo tradicional, refinada visualmente)
              ============================================================ */}
          <motion.section {...sectionFade(0.25)} className="mep-section">
            <SectionMarker
              number="04"
              title="Jerarquía clásica"
              subtitle="Selección por tema, subtema y subtema específico — clásico"
              hint={
                directCount > 0 ? (
                  <span style={{ color: T.inkMute }}>desactivada por selección directa</span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <span>
                      {selectedTopics.length}t · {selectedSubtopics.length}s · {selectedSubtopicsSpecific.length}sp
                    </span>
                    {hierarchyFiltersCount > 0 && (
                      <>
                        <span style={{ color: T.hairline }}>·</span>
                        <button
                          onClick={clearHierarchyFilters}
                          className="mep-cta-ghost"
                          style={{
                            color: T.accent,
                            fontSize: '10px',
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                          }}
                        >
                          limpiar →
                        </button>
                      </>
                    )}
                  </span>
                )
              }
            />

            {directCount > 0 ? (
              <div
                className="px-7 py-8 text-center relative"
                style={{
                  background: darkMode
                    ? `linear-gradient(165deg, ${T.paperSoft} 0%, ${T.paper} 100%)`
                    : `linear-gradient(165deg, ${T.paperSoft} 0%, ${T.paperWarm} 100%)`,
                  border: `1px solid ${T.hairline}`,
                  boxShadow: darkMode
                    ? `inset 0 1px 0 rgba(242, 237, 224, 0.025)`
                    : `inset 0 1px 0 rgba(255,255,255,0.55)`,
                }}
              >
                <span
                  className="mep-cap mb-3 inline-block"
                  style={{ color: T.inkMute, letterSpacing: '0.28em' }}
                >
                  ⏸ pausada
                </span>
                <p className="mep-figure text-base mb-3" style={{ color: T.inkSoft, lineHeight: 1.5 }}>
                  La jerarquía clásica está en pausa.{' '}
                  <strong
                    style={{
                      color: T.accent,
                      fontWeight: 500,
                      fontFamily: '"Geist Mono", monospace',
                    }}
                  >
                    {directCount}
                  </strong>{' '}
                  {directCount === 1 ? 'pregunta seleccionada directamente' : 'preguntas seleccionadas directamente'} desde § 03.
                </p>
                <button
                  onClick={clearDirectSelection}
                  className="mep-cta-secondary mt-2"
                >
                  limpiar y volver a jerarquía →
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                {/* TEMAS */}
                <div className="mep-control-card">
                  <div className="mep-control-card-corner" />
                  <div className="flex items-baseline justify-between mb-1">
                    <h3
                      style={{
                        fontFamily: '"Newsreader", serif',
                        fontSize: '1.5rem',
                        fontWeight: 400,
                        color: T.ink,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      Temas
                    </h3>
                    <span className="mep-mono text-[10px]" style={{ color: T.inkMute, letterSpacing: '0.18em' }}>
                      {selectedTopics.length}/{filteredTopicsWithCount.length}
                    </span>
                  </div>
                  <p className="text-xs mep-sans mb-4" style={{ color: T.inkSoft }}>
                    nivel jerárquico mayor
                  </p>
                  <div className="relative mb-3">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.inkMute }} />
                    <input
                      type="text"
                      value={topicSearch}
                      onChange={(e) => setTopicSearch(e.target.value)}
                      placeholder="filtrar temas…"
                      className="mep-input w-full pl-9 pr-3 py-2 text-sm"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  <div className="space-y-1 max-h-[26rem] overflow-y-auto pr-1 -ml-3">
                    {filteredTopicsWithCount.length === 0 ? (
                      <p className="mep-figure text-xs px-3 py-2">no hay temas</p>
                    ) : (
                      filteredTopicsWithCount.map((item) => {
                        const sel = selectedTopics.includes(item.topic);
                        const pct = Math.max(4, Math.round((item.count / (maxTopicCount || 1)) * 100));
                        return (
                          <button
                            key={`tp-${item.topic}`}
                            data-selected={sel}
                            onClick={() => toggleTopic(item.topic)}
                            className="mep-prow w-full text-left flex-col items-stretch !gap-1.5"
                            style={{ alignItems: 'stretch' }}
                          >
                            <div className="flex items-baseline gap-2">
                              <span
                                className="mep-prow-label text-[13.5px] flex-1 mep-sans truncate"
                                style={{ color: T.ink }}
                              >
                                {item.topic}
                              </span>
                              <span
                                className="mep-mono text-[11px]"
                                style={{ color: sel ? T.accent : T.inkMute }}
                              >
                                {item.count.toLocaleString()}
                              </span>
                            </div>
                            <div className="mep-bar-track">
                              <div
                                className="mep-bar-fill"
                                style={{
                                  width: `${pct}%`,
                                  background: sel ? T.accent : T.inkMute,
                                  boxShadow: sel && darkMode ? `0 0 8px ${T.glow}` : 'none',
                                }}
                              />
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* SUBTEMAS */}
                <div className="mep-control-card">
                  <div className="mep-control-card-corner" />
                  <div className="flex items-baseline justify-between mb-1">
                    <h3
                      style={{
                        fontFamily: '"Newsreader", serif',
                        fontSize: '1.5rem',
                        fontWeight: 400,
                        color: T.ink,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      Subtemas
                    </h3>
                    <span className="mep-mono text-[10px]" style={{ color: T.inkMute, letterSpacing: '0.18em' }}>
                      {selectedSubtopics.length}/{filteredSubtopicsWithCount.length}
                    </span>
                  </div>
                  <p className="text-xs mep-sans mb-4" style={{ color: T.inkSoft }}>
                    refinamiento intermedio
                  </p>
                  <div className="relative mb-3">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.inkMute }} />
                    <input
                      type="text"
                      value={subtopicSearch}
                      onChange={(e) => setSubtopicSearch(e.target.value)}
                      placeholder="filtrar subtemas…"
                      className="mep-input w-full pl-9 pr-3 py-2 text-sm"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  <div className="space-y-px max-h-[26rem] overflow-y-auto pr-1 -ml-3">
                    {filteredSubtopicsWithCount.length === 0 ? (
                      <p className="mep-figure text-xs px-3 py-2">selecciona un tema primero</p>
                    ) : (
                      filteredSubtopicsWithCount.map((item) => {
                        const sel = selectedSubtopics.includes(item.subtopic);
                        return (
                          <button
                            key={`sb-${item.subtopic}`}
                            data-selected={sel}
                            onClick={() => toggleSubtopic(item.subtopic)}
                            className="mep-prow w-full text-left"
                          >
                            <span
                              className="mep-prow-label text-[13.5px] flex-1 mep-sans truncate"
                              style={{ color: T.ink }}
                            >
                              {item.subtopic}
                            </span>
                            <span
                              className="mep-mono text-[11px]"
                              style={{ color: sel ? T.accent : T.inkMute }}
                            >
                              {item.count.toLocaleString()}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* SUBTEMAS ESPECÍFICOS */}
                <div className="mep-control-card">
                  <div className="mep-control-card-corner" />
                  <div className="flex items-baseline justify-between mb-1">
                    <h3
                      style={{
                        fontFamily: '"Newsreader", serif',
                        fontSize: '1.5rem',
                        fontWeight: 400,
                        color: T.ink,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      Específicos
                    </h3>
                    <span className="mep-mono text-[10px]" style={{ color: T.inkMute, letterSpacing: '0.18em' }}>
                      {selectedSubtopicsSpecific.length}/{filteredSubtopicsSpecificWithCount.length}
                    </span>
                  </div>
                  <p className="text-xs mep-sans mb-4" style={{ color: T.inkSoft }}>
                    nivel más fino del archivo
                  </p>
                  <div className="relative mb-3">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.inkMute }} />
                    <input
                      type="text"
                      value={subtopicSpecificSearch}
                      onChange={(e) => setSubtopicSpecificSearch(e.target.value)}
                      placeholder="filtrar específicos…"
                      className="mep-input w-full pl-9 pr-3 py-2 text-sm"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  <div className="space-y-px max-h-[26rem] overflow-y-auto pr-1 -ml-3">
                    {filteredSubtopicsSpecificWithCount.length === 0 ? (
                      <p className="mep-figure text-xs px-3 py-2">selecciona un subtema</p>
                    ) : (
                      filteredSubtopicsSpecificWithCount.map((item) => {
                        const sel = selectedSubtopicsSpecific.includes(item.subtopic_specific);
                        return (
                          <button
                            key={`sps-${item.subtopic_specific}`}
                            data-selected={sel}
                            onClick={() => toggleSubtopicSpecific(item.subtopic_specific)}
                            className="mep-prow w-full text-left"
                          >
                            <span
                              className="mep-prow-label text-[13.5px] flex-1 mep-sans truncate"
                              style={{ color: T.ink }}
                            >
                              {item.subtopic_specific}
                            </span>
                            <span
                              className="mep-mono text-[11px]"
                              style={{ color: sel ? T.accent : T.inkMute }}
                            >
                              {item.count.toLocaleString()}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

              </div>
            )}
          </motion.section>

          {/* ============================================================
              § 05 · FILTROS ACTIVOS — chips premium con motion
              ============================================================ */}
          <motion.section {...sectionFade(0.3)} className="mep-section">
            <SectionMarker
              number="05"
              title="Filtros activos"
              subtitle="Resumen de la composición — clic en un chip para retirarlo"
              hint={
                (selectedYears.length + selectedEspecialidades.length + selectedAreas.length +
                 selectedTopics.length + selectedSubtopics.length + selectedSubtopicsSpecific.length +
                 (directCount > 0 ? 1 : 0)) + ' chips'
              }
            />

            <div
              className="p-5 relative"
              style={{
                background: T.paperSoft,
                border: `1px solid ${T.hairline}`,
                boxShadow: darkMode
                  ? `inset 0 1px 0 rgba(242, 237, 224, 0.025)`
                  : `inset 0 1px 0 rgba(255,255,255,0.55)`,
              }}
            >
              <div className="flex flex-wrap gap-2 items-center">
                <AnimatePresence>
                  {directCount > 0 && (
                    <motion.button
                      key="dir"
                      initial={{ opacity: 0, scale: 0.85, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85, y: -4 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="mep-chip"
                      data-variant="accent"
                      onClick={clearDirectSelection}
                    >
                      <span className="mep-cap" style={{ letterSpacing: '0.16em', fontSize: '9px' }}>
                        directa
                      </span>
                      · {directCount}
                      <span className="mep-chip-x"><X size={11} /></span>
                    </motion.button>
                  )}
                  {selectedYears.map((year) => (
                    <motion.button
                      key={`year-${year}`}
                      initial={{ opacity: 0, scale: 0.85, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85, y: -4 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="mep-chip"
                      data-variant="mono"
                      onClick={() => setSelectedYears((prev) => prev.filter((y) => y !== year))}
                    >
                      {year}
                      <span className="mep-chip-x"><X size={11} /></span>
                    </motion.button>
                  ))}
                  {selectedEspecialidades.map((esp) => (
                    <motion.button
                      key={`esp-${esp}`}
                      initial={{ opacity: 0, scale: 0.85, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85, y: -4 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="mep-chip"
                      onClick={() => setSelectedEspecialidades((prev) => prev.filter((e) => e !== esp))}
                    >
                      {esp}
                      <span className="mep-chip-x"><X size={11} /></span>
                    </motion.button>
                  ))}
                  {selectedAreas.map((area) => (
                    <motion.button
                      key={`area-${area}`}
                      initial={{ opacity: 0, scale: 0.85, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85, y: -4 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="mep-chip"
                      onClick={() => setSelectedAreas((prev) => prev.filter((a) => a !== area))}
                    >
                      {area}
                      <span className="mep-chip-x"><X size={11} /></span>
                    </motion.button>
                  ))}
                  {selectedTopics.map((tp) => (
                    <motion.button
                      key={`top-${tp}`}
                      initial={{ opacity: 0, scale: 0.85, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85, y: -4 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="mep-chip"
                      onClick={() => setSelectedTopics((prev) => prev.filter((t) => t !== tp))}
                    >
                      {tp}
                      <span className="mep-chip-x"><X size={11} /></span>
                    </motion.button>
                  ))}
                  {selectedSubtopics.map((st) => (
                    <motion.button
                      key={`sub-${st}`}
                      initial={{ opacity: 0, scale: 0.85, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85, y: -4 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="mep-chip"
                      onClick={() => setSelectedSubtopics((prev) => prev.filter((s) => s !== st))}
                    >
                      {st}
                      <span className="mep-chip-x"><X size={11} /></span>
                    </motion.button>
                  ))}
                  {selectedSubtopicsSpecific.map((sps) => (
                    <motion.button
                      key={`sps-${sps}`}
                      initial={{ opacity: 0, scale: 0.85, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85, y: -4 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="mep-chip"
                      onClick={() => setSelectedSubtopicsSpecific((prev) => prev.filter((v) => v !== sps))}
                    >
                      {sps}
                      <span className="mep-chip-x"><X size={11} /></span>
                    </motion.button>
                  ))}
                </AnimatePresence>

                {(selectedYears.length === 0 &&
                  selectedEspecialidades.length === 0 &&
                  selectedAreas.length === 0 &&
                  selectedTopics.length === 0 &&
                  selectedSubtopics.length === 0 &&
                  selectedSubtopicsSpecific.length === 0 &&
                  directCount === 0) && (
                  <p className="mep-figure text-sm w-full text-center py-2" style={{ color: T.inkMute }}>
                    ningún filtro activo · todo el archivo está disponible
                  </p>
                )}

                {(selectedYears.length + selectedEspecialidades.length + selectedAreas.length +
                  selectedTopics.length + selectedSubtopics.length + selectedSubtopicsSpecific.length +
                  (directCount > 0 ? 1 : 0)) > 0 && (
                  <button
                    onClick={resetFilters}
                    className="mep-cta-ghost ml-auto self-center"
                    style={{ color: T.accent, fontSize: '11px' }}
                  >
                    limpiar todo →
                  </button>
                )}
              </div>
            </div>
          </motion.section>

          {/* ============================================================
              § 06 · CONFIGURACIÓN — control cards premium
              ============================================================ */}
          <motion.section {...sectionFade(0.35)} className="mep-section">
            <SectionMarker
              number="06"
              title="Configuración"
              subtitle="Detalles operativos del examen"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* TÍTULO */}
              <div className="mep-control-card">
                <div className="mep-control-card-corner" />
                <label className="mep-cap block mb-2" style={{ color: T.inkMute, letterSpacing: '0.24em' }}>
                  Título · opcional
                </label>
                <input
                  type="text"
                  value={examTitle}
                  onChange={(e) => setExamTitle(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleStart(); } }}
                  placeholder="Ej: Verificación Meningitis · Ronda 1"
                  className="w-full bg-transparent outline-none border-none"
                  style={{
                    fontFamily: '"Newsreader", serif',
                    fontStyle: 'italic',
                    fontSize: '1.5rem',
                    color: T.ink,
                    padding: '4px 0 6px',
                    borderBottom: `1px solid ${examTitle ? T.accent : T.hairline}`,
                    transition: 'border-color 0.25s',
                  }}
                />
                <p className="text-[11px] mep-sans mt-3" style={{ color: T.inkMute }}>
                  ↳ aparecerá en el historial · Enter para iniciar directamente
                </p>
              </div>

              {/* MODO */}
              <div className="mep-control-card">
                <div className="mep-control-card-corner" />
                <label className="mep-cap block mb-3" style={{ color: T.inkMute, letterSpacing: '0.24em' }}>
                  Modo
                </label>
                <div className="mep-segment w-full">
                  <button
                    onClick={() => setExamMode('study')}
                    data-active={examMode === 'study'}
                    className="mep-segment-btn flex-1"
                  >
                    Estudio
                  </button>
                  <button
                    onClick={() => setExamMode('simulation')}
                    data-active={examMode === 'simulation'}
                    data-variant="accent"
                    className="mep-segment-btn flex-1"
                  >
                    Simulacro
                  </button>
                </div>
                <p className="text-[11px] mep-sans mt-3" style={{ color: T.inkMute }}>
                  {examMode === 'study'
                    ? '↳ feedback inmediato · sin cronómetro · multiintento'
                    : '↳ tiempo limitado · resultados al final · una sola opción'}
                </p>
                {examMode === 'simulation' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 pt-4 flex items-center gap-3 text-xs"
                    style={{ borderTop: `1px dashed ${T.hairlineSoft}` }}
                  >
                    <span className="mep-cap" style={{ color: T.inkMute }}>Orden:</span>
                    <button
                      onClick={() => setShuffleQuestions(true)}
                      className="mep-chip"
                      data-variant={shuffleQuestions ? 'accent' : undefined}
                      style={{ padding: '4px 10px' }}
                    >
                      🔀 aleatorio
                    </button>
                    <button
                      onClick={() => setShuffleQuestions(false)}
                      className="mep-chip"
                      data-variant={!shuffleQuestions ? 'accent' : undefined}
                      style={{ padding: '4px 10px' }}
                    >
                      ▤ orden fijo
                    </button>
                  </motion.div>
                )}
              </div>

              {/* NÚMERO DE PREGUNTAS */}
              <div className="mep-control-card">
                <div className="mep-control-card-corner" />
                <div className="flex items-baseline justify-between mb-1">
                  <label className="mep-cap" style={{ color: T.inkMute, letterSpacing: '0.24em' }}>
                    Preguntas
                  </label>
                  <div className="flex items-baseline gap-1.5">
                    <span
                      className="mep-mono leading-none"
                      style={{
                        color: T.ink,
                        fontWeight: 500,
                        fontSize: '2.5rem',
                        textShadow: darkMode ? `0 0 18px ${T.glow}` : 'none',
                      }}
                    >
                      {numPreguntas}
                    </span>
                    <span className="mep-cap" style={{ color: T.inkMute }}>de {maxAllowed}</span>
                  </div>
                </div>
                <p className="text-xs mep-sans mb-5" style={{ color: T.inkSoft }}>
                  cantidad de preguntas a tomar
                </p>
                <input
                  type="range"
                  value={numPreguntas}
                  onChange={(e) => setNumPreguntas(Math.min(Number(e.target.value), maxAllowed))}
                  min={1}
                  max={maxAllowed}
                  className="mep-slider"
                />
                <div className="flex justify-between text-[10px] mt-2 mep-mono" style={{ color: T.inkMute }}>
                  <span>1</span>
                  <span>{Math.floor(maxAllowed / 2)}</span>
                  <span>{maxAllowed}</span>
                </div>
                {maxAllowed < filteredQuestions.length && (
                  <p
                    className="text-xs mt-3 pt-3"
                    style={{
                      color: T.accent,
                      fontFamily: '"Newsreader", serif',
                      fontStyle: 'italic',
                      borderTop: `1px dashed ${T.hairlineSoft}`,
                    }}
                  >
                    {!user
                      ? '⚠ modo invitado: límite 5 — inicia sesión gratis para 15'
                      : subscription?.plan === 'free'
                        ? '⚠ plan gratuito: límite 15 — actualiza a PRO para ilimitadas'
                        : null}
                  </p>
                )}
              </div>

              {/* TIEMPO */}
              <div className="mep-control-card" style={{ opacity: examMode === 'study' ? 0.6 : 1 }}>
                <div className="mep-control-card-corner" style={{ opacity: examMode === 'study' ? 0 : 1 }} />
                <div className="flex items-baseline justify-between mb-1">
                  <label className="mep-cap" style={{ color: T.inkMute, letterSpacing: '0.24em' }}>
                    Tiempo
                  </label>
                  <div className="flex items-baseline gap-1.5">
                    <span
                      className="mep-mono leading-none"
                      style={{
                        color: examMode === 'study' ? T.inkMute : T.ink,
                        fontWeight: 500,
                        fontSize: '2.5rem',
                        textShadow: examMode !== 'study' && darkMode ? `0 0 18px ${T.glow}` : 'none',
                      }}
                    >
                      {examMode === 'study' ? '∞' : tiempo}
                    </span>
                    {examMode !== 'study' && (
                      <span className="mep-cap" style={{ color: T.inkMute }}>min</span>
                    )}
                  </div>
                </div>
                <p className="text-xs mep-sans mb-5" style={{ color: T.inkSoft }}>
                  {examMode === 'study' ? 'sin límite en modo estudio' : 'duración del cronómetro'}
                </p>
                <input
                  type="range"
                  value={tiempo}
                  onChange={(e) => setTiempo(Number(e.target.value))}
                  min={1}
                  max={180}
                  disabled={examMode === 'study'}
                  className="mep-slider"
                />
                <div className="flex justify-between text-[10px] mt-2 mep-mono" style={{ color: T.inkMute }}>
                  <span>1</span>
                  <span>90</span>
                  <span>180</span>
                </div>
              </div>

            </div>
          </motion.section>

          {/* ============================================================
              § 07 · ENSAMBLAJE FINAL — magazine cover style
              ============================================================ */}
          <motion.section
            {...sectionFade(0.4)}
            id="filtered-questions-section"
            className="mep-section"
          >
            <SectionMarker
              number="07"
              title="Ensamblaje"
              subtitle="Resumen y orden de inicio"
              accent
              hint={`${filteredQuestions.length}/${sourceQuestions.length}`}
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">

              {/* Columna izquierda — el número HERO + stats */}
              <div className="lg:col-span-7 flex flex-col justify-between">
                <div>
                  <p className="mep-cap mb-2" style={{ color: T.accent, letterSpacing: '0.32em' }}>
                    ⟢ disponibles tras filtros
                  </p>

                  {/* Número GIGANTESCO */}
                  <div className="flex items-start gap-5 flex-wrap">
                    <span
                      className="mep-mono leading-[0.85]"
                      style={{
                        fontSize: 'clamp(4.5rem, 14vw, 10rem)',
                        color: T.ink,
                        fontWeight: 400,
                        letterSpacing: '-0.05em',
                        textShadow: darkMode
                          ? `0 0 60px rgba(242, 237, 224, 0.10), 0 0 120px ${T.glow}`
                          : 'none',
                      }}
                    >
                      {filteredQuestions.length.toLocaleString()}
                    </span>
                    <div className="pt-4">
                      <p
                        className="mep-figure text-lg"
                        style={{ color: T.inkSoft }}
                      >
                        de {sourceQuestions.length.toLocaleString()}
                      </p>
                      <p className="mep-cap mt-1" style={{ color: T.inkMute }}>
                        en archivo
                      </p>
                    </div>
                  </div>

                  {/* Barra de progreso premium */}
                  <div
                    className="mt-6 h-1 relative"
                    style={{ background: T.hairlineSoft }}
                  >
                    <motion.div
                      className="absolute inset-y-0 left-0"
                      style={{
                        background: `linear-gradient(90deg, ${T.accent} 0%, ${darkMode ? T.accentDeep : T.accentDeep} 100%)`,
                        boxShadow: darkMode ? `0 0 12px ${T.glow}` : 'none',
                      }}
                      initial={{ width: 0 }}
                      animate={{
                        width: `${sourceQuestions.length > 0 ? (filteredQuestions.length / sourceQuestions.length) * 100 : 0}%`,
                      }}
                      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                  <div className="flex justify-between mt-1.5 mep-mono text-[10px]" style={{ color: T.inkMute }}>
                    <span>0</span>
                    <span>
                      {sourceQuestions.length > 0
                        ? `${((filteredQuestions.length / sourceQuestions.length) * 100).toFixed(1)}% del archivo`
                        : ''}
                    </span>
                    <span>{sourceQuestions.length.toLocaleString()}</span>
                  </div>
                </div>

                {/* 3 stats premium con jerarquía editorial */}
                <div
                  className="grid grid-cols-3 gap-x-4 mt-10 pt-5"
                  style={{ borderTop: `1px solid ${T.hairline}` }}
                >
                  <div>
                    <p className="mep-cap" style={{ color: T.inkMute }}>tomarás</p>
                    <p
                      className="mep-mono mt-2"
                      style={{
                        color: T.ink,
                        fontSize: '2.25rem',
                        fontWeight: 500,
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {numPreguntas}
                    </p>
                    <p
                      className="mep-figure text-xs mt-1"
                      style={{ color: T.inkSoft }}
                    >
                      {numPreguntas === 1 ? 'pregunta' : 'preguntas'}
                    </p>
                  </div>
                  <div>
                    <p className="mep-cap" style={{ color: T.inkMute }}>tiempo</p>
                    <p
                      className="mep-mono mt-2"
                      style={{
                        color: T.ink,
                        fontSize: '2.25rem',
                        fontWeight: 500,
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {examMode === 'study' ? '∞' : tiempo}
                    </p>
                    <p
                      className="mep-figure text-xs mt-1"
                      style={{ color: T.inkSoft }}
                    >
                      {examMode === 'study' ? 'sin límite' : 'minutos'}
                    </p>
                  </div>
                  <div>
                    <p className="mep-cap" style={{ color: T.inkMute }}>modo</p>
                    <p
                      className="mt-2 italic"
                      style={{
                        color: examMode === 'simulation' ? T.accent : T.ink,
                        fontSize: '1.5rem',
                        fontFamily: '"Newsreader", serif',
                        fontWeight: 400,
                        lineHeight: 1.1,
                        letterSpacing: '-0.01em',
                        textShadow: examMode === 'simulation' && darkMode ? `0 0 14px ${T.glow}` : 'none',
                      }}
                    >
                      {examMode === 'study' ? 'estudio' : 'simulacro'}
                    </p>
                    <p
                      className="mep-figure text-xs mt-1"
                      style={{ color: T.inkSoft }}
                    >
                      {examMode === 'study' ? 'feedback inmediato' : 'evaluación al final'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Columna derecha — CTA card como portada */}
              <div className="lg:col-span-5">
                <div
                  className="relative"
                  style={{
                    background: darkMode
                      ? `linear-gradient(160deg, ${T.paperLift} 0%, ${T.paperSoft} 50%, ${T.paper} 100%)`
                      : `linear-gradient(160deg, ${T.paperSoft} 0%, ${T.paperWarm} 100%)`,
                    border: `1px solid ${T.hairline}`,
                    padding: '36px 28px 28px',
                    boxShadow: darkMode
                      ? `inset 0 1px 0 rgba(242, 237, 224, 0.05), 0 32px 80px -20px rgba(0, 0, 0, 0.7), 0 0 0 1px ${T.hairlineSoft}`
                      : `inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 24px 56px -20px rgba(14, 18, 24, 0.20)`,
                  }}
                >
                  {/* 4 corner marks editoriales */}
                  <div className="absolute top-0 left-0 w-9 h-px" style={{ background: T.accent }} />
                  <div className="absolute top-0 left-0 w-px h-9" style={{ background: T.accent }} />
                  <div className="absolute top-0 right-0 w-9 h-px" style={{ background: T.accent }} />
                  <div className="absolute top-0 right-0 w-px h-9" style={{ background: T.accent }} />
                  <div className="absolute bottom-0 left-0 w-9 h-px" style={{ background: T.accent }} />
                  <div className="absolute bottom-0 left-0 w-px h-9" style={{ background: T.accent }} />
                  <div className="absolute bottom-0 right-0 w-9 h-px" style={{ background: T.accent }} />
                  <div className="absolute bottom-0 right-0 w-px h-9" style={{ background: T.accent }} />

                  {/* Etiqueta */}
                  <div className="mb-4">
                    <p
                      className="mep-cap"
                      style={{ color: T.accent, letterSpacing: '0.32em' }}
                    >
                      ⟢ próximo · examen
                    </p>
                  </div>

                  {/* Título o descripción ceremonial */}
                  <p
                    className="mb-7 leading-snug"
                    style={{
                      color: T.ink,
                      fontFamily: '"Newsreader", serif',
                      fontSize: examTitle ? '1.625rem' : '1.25rem',
                      fontStyle: examTitle ? 'normal' : 'italic',
                      fontWeight: 400,
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {examTitle ? (
                      <>
                        <span style={{ color: T.accent }}>"</span>
                        {examTitle}
                        <span style={{ color: T.accent }}>"</span>
                      </>
                    ) : (
                      <>
                        Una sesión de{' '}
                        <em style={{ color: T.accent, fontStyle: 'italic' }}>
                          {numPreguntas}
                        </em>{' '}
                        {numPreguntas === 1 ? 'pregunta' : 'preguntas'}{' '}
                        {examMode === 'simulation'
                          ? `cronometrada en ${tiempo} minutos`
                          : 'sin reloj'}
                        .
                      </>
                    )}
                  </p>

                  {/* CTA principal */}
                  <button
                    onClick={handleStart}
                    disabled={filteredQuestions.length === 0}
                    className="mep-cta w-full justify-between"
                  >
                    <span style={{ fontFamily: '"Manrope", sans-serif', fontWeight: 600, letterSpacing: '0.04em' }}>
                      Comenzar examen
                    </span>
                    <ChevronRight size={20} />
                  </button>

                  {filteredQuestions.length === 0 && (
                    <p
                      className="mep-figure text-xs mt-4 text-center"
                      style={{ color: T.accent, fontStyle: 'italic' }}
                    >
                      no hay preguntas disponibles con estos filtros
                    </p>
                  )}

                  {/* Footnote */}
                  <p
                    className="mep-cap mt-5 text-center"
                    style={{
                      color: T.inkMute,
                      letterSpacing: '0.20em',
                      fontSize: '9.5px',
                    }}
                  >
                    ⏎ enter desde el título también inicia
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* ============================================================
              COLOFÓN — pequeño cierre editorial
              ============================================================ */}
          <motion.div
            {...sectionFade(0.45)}
            className="mt-20 pt-8 flex items-center justify-between"
            style={{ borderTop: `1px solid ${T.hairlineSoft}` }}
          >
            <p className="mep-cap" style={{ color: T.inkMute }}>
              MediExam Pro · módulo de composición
            </p>
            <p className="mep-figure text-xs">
              <em>"el conocimiento salva vidas"</em>
            </p>
          </motion.div>

        </div>
      </div>
    </>
  );
}

export default GenerateExam;
